import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { storeAdminToken, adminFetch } from '../utils/adminApi';
import './AdminPanel.css';
import SeoFieldsForm from './admin/SeoFieldsForm';
import TechnicalSeoPanel from './admin/TechnicalSeoPanel';
import PagesCmsPanel from './admin/PagesCmsPanel';
import BlogAdminPanel from './admin/BlogAdminPanel';
import LandingPagesPanel from './admin/LandingPagesPanel';
import { appendSeoToFormData, pickSeoFromEntity, emptySeoFields, slugify } from '../utils/seoFormHelpers';

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('products');

  // Database states
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [brandProducts, setBrandProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [dbRelatedProducts, setDbRelatedProducts] = useState([]);
  const [arrivals, setArrivals] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderSearchQuery, setOrderSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [moneybirdBusy, setMoneybirdBusy] = useState(false);
  const [shippingCharge, setShippingCharge] = useState(0);
  const [shippingInput, setShippingInput] = useState('');

  // Loading & Notification states
  const [loading, setLoading] = useState(false);

  // CSV Import Verification Modal States
  const [importVerificationData, setImportVerificationData] = useState(null);
  const [showImportPreviewModal, setShowImportPreviewModal] = useState(false);
  const [previewFilterTab, setPreviewFilterTab] = useState('ALL');
  const [importConfirming, setImportConfirming] = useState(false);

  // Form states - Product
  const [productForm, setProductForm] = useState({
    name: '',
    price: '',
    originalPrice: '',
    b2bPrice: '',
    rating: '5.0',
    category: '',
    subcategory: '',
    brand: '',
    description: '',
    keywords: '',
    specification: '',
    stockStatus: 'In Stock',
    metaTitle: '',
    metaDescription: '',
    quantity: '0',
    shippingCharges: '0',
    deliveryTime: '',
    ...emptySeoFields,
  });
  const [productKeywordInput, setProductKeywordInput] = useState('');
  const [editKeywordInput, setEditKeywordInput] = useState('');
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [editProductSearchQuery, setEditProductSearchQuery] = useState('');
  const [productImageFiles, setProductImageFiles] = useState([]);
  const [productImagePreviews, setProductImagePreviews] = useState([]);
  const [productImageError, setProductImageError] = useState('');
  const [productVideoFile, setProductVideoFile] = useState(null);
  const [productVideoPreview, setProductVideoPreview] = useState('');
  const [productVideoError, setProductVideoError] = useState('');
  const [selectedRelatedProducts, setSelectedRelatedProducts] = useState([]);

  // Edit Product states
  const [editingProduct, setEditingProduct] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    price: '',
    originalPrice: '',
    b2bPrice: '',
    serviceCost: '',
    rating: '5.0',
    category: '',
    subcategory: '',
    description: '',
    keywords: '',
    specification: '',
    stockStatus: 'In Stock',
    metaTitle: '',
    metaDescription: '',
    brandName: '',
    model: '',
    fault: '',
    deliveryTime: '',
    brand: '',
    quantity: '0',
    shippingCharges: '0',
    brandLogo: '',
    ...emptySeoFields,
  });
  const [editImageFiles, setEditImageFiles] = useState([]);
  const [editImagePreviews, setEditImagePreviews] = useState([]);
  const [editExistingImages, setEditExistingImages] = useState([]);
  const [editImageError, setEditImageError] = useState('');
  const [editVideoFile, setEditVideoFile] = useState(null);
  const [editVideoPreview, setEditVideoPreview] = useState('');
  const [editExistingVideoUrl, setEditExistingVideoUrl] = useState('');
  const [editVideoError, setEditVideoError] = useState('');
  const [editSelectedRelatedProducts, setEditSelectedRelatedProducts] = useState([]);

  // Form states - Related Product
  const [relatedForm, setRelatedForm] = useState({
    name: '',
    price: '',
    originalPrice: '',
    b2bPrice: '',
    rating: '5.0',
    category: '',
    description: '',
    stockStatus: 'In Stock',
    metaTitle: '',
    metaDescription: '',
    deliveryTime: '',
  });
  const [relatedImageFiles, setRelatedImageFiles] = useState([]);
  const [relatedImagePreviews, setRelatedImagePreviews] = useState([]);
  const [relatedImageError, setRelatedImageError] = useState('');
  const [relatedVideoFile, setRelatedVideoFile] = useState(null);
  const [relatedVideoPreview, setRelatedVideoPreview] = useState('');
  const [relatedVideoError, setRelatedVideoError] = useState('');

  // Form states - Category
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    iconType: 'preset', // 'preset' or 'custom'
    presetIcon: 'phone',
    customIconSvg: '',
    ...emptySeoFields,
  });
  const [categoryBrandLogoFile, setCategoryBrandLogoFile] = useState(null);
  const [categoryBrandLogoPreview, setCategoryBrandLogoPreview] = useState('');
  const [categoryBrandLogoError, setCategoryBrandLogoError] = useState('');
  const [editingCategorySeoId, setEditingCategorySeoId] = useState('');

  // Form states - Sub Category
  const [selectedParentCategory, setSelectedParentCategory] = useState('');
  const [subcategoryNameInput, setSubcategoryNameInput] = useState('');

  // Form states - Brand
  const [brandForm, setBrandForm] = useState({
    name: '',
    ...emptySeoFields,
  });
  const [brandLogoFile, setBrandLogoFile] = useState(null);
  const [brandLogoPreview, setBrandLogoPreview] = useState('');
  const [brandLogoError, setBrandLogoError] = useState('');
  const [editingBrandSeoId, setEditingBrandSeoId] = useState('');

  // Form states - Brand Product
  const [brandProductForm, setBrandProductForm] = useState({
    name: '',
    price: '',
    originalPrice: '',
    b2bPrice: '',
    rating: '5.0',
    brand: '',
    description: '',
    keywords: '',
    specification: '',
    stockStatus: 'In Stock',
    metaTitle: '',
    metaDescription: '',
    quantity: '0',
    shippingCharges: '0',
    deliveryTime: '',
    ...emptySeoFields,
  });
  const [brandProductKeywordInput, setBrandProductKeywordInput] = useState('');
  const [brandProductSearchQuery, setBrandProductSearchQuery] = useState('');
  const [brandProductImageFiles, setBrandProductImageFiles] = useState([]);
  const [brandProductImagePreviews, setBrandProductImagePreviews] = useState([]);
  const [brandProductImageError, setBrandProductImageError] = useState('');
  const [brandProductVideoFile, setBrandProductVideoFile] = useState(null);
  const [brandProductVideoPreview, setBrandProductVideoPreview] = useState('');
  const [brandProductVideoError, setBrandProductVideoError] = useState('');
  const [brandProductSelectedRelatedProducts, setBrandProductSelectedRelatedProducts] = useState([]);

  // Form states - New Arrival
  const [arrivalForm, setArrivalForm] = useState({
    title: '',
    description: '',
    layoutStyle: 'large-card',
    link: '#explore-products',
  });
  const [arrivalImageFile, setArrivalImageFile] = useState(null);
  const [arrivalImagePreview, setArrivalImagePreview] = useState(null);
  const [arrivalImageError, setArrivalImageError] = useState('');

  // Form states - Service
  const [serviceForm, setServiceForm] = useState({
    name: '',
    price: '',
    originalPrice: '',
    b2bPrice: '',
    serviceCost: '',
    shippingCharges: '0',
    category: 'Services',
    description: '',
    brandName: '',
    model: '',
    fault: '',
    deliveryTime: '',
    stockStatus: 'In Stock'
  });
  const [serviceImageFiles, setServiceImageFiles] = useState([]);
  const [serviceImagePreviews, setServiceImagePreviews] = useState([]);
  const [serviceImageError, setServiceImageError] = useState('');
  const [serviceBrandLogoFile, setServiceBrandLogoFile] = useState(null);
  const [serviceBrandLogoPreview, setServiceBrandLogoPreview] = useState('');
  const [serviceBrandLogoError, setServiceBrandLogoError] = useState('');
  const [editServiceBrandLogoFile, setEditServiceBrandLogoFile] = useState(null);
  const [editServiceBrandLogoPreview, setEditServiceBrandLogoPreview] = useState('');
  const [adminSearchQuery, setAdminSearchQuery] = useState('');

  // Preset Icons map
  const PRESET_ICONS = {
    phone: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2" /><line x1="12" y1="18" x2="12.01" y2="18" /></svg>`,
    headphones: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6" /><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" /></svg>`,
    laptop: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><line x1="2" y1="20" x2="22" y2="20" /><line x1="12" y1="17" x2="12" y2="20" /></svg>`,
    gamepad: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="6" y1="12" x2="10" y2="12" /><line x1="8" y1="10" x2="8" y2="14" /><line x1="15" y1="13" x2="15.01" y2="13" /><line x1="18" y1="11" x2="18.01" y2="11" /><rect x="2" y="6" width="20" height="12" rx="3" /></svg>`,
    tv: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="20" height="13" rx="2" /><path d="M17 2l-5 5-5-5" /></svg>`,
    offers: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82zM7 7h.01" /></svg>`
  };

  // Fetch Database Information
  const fetchData = async () => {
    try {
      const catRes = await fetch('/api/categories');
      const catData = await catRes.json();
      setCategories(catData);

      const prodRes = await fetch('/api/products');
      const prodData = await prodRes.json();
      setProducts(prodData);

      const servRes = await fetch('/api/services');
      const servData = await servRes.json();
      setServices(servData);

      const arrRes = await fetch('/api/new-arrivals');
      const arrData = await arrRes.json();
      setArrivals(arrData);

      const ordRes = await fetch('/api/orders');
      const ordData = await ordRes.json();
      setOrders(ordData);

      const relRes = await fetch('/api/related-products');
      const relData = await relRes.json();
      setDbRelatedProducts(relData);

      const brandRes = await fetch('/api/brands');
      const brandData = await brandRes.json();
      setBrands(brandData);

      const brandProdRes = await fetch('/api/brand-products');
      const brandProdData = await brandProdRes.json();
      setBrandProducts(brandProdData);

      const shipRes = await fetch('/api/shipping');
      if (shipRes.ok) {
        const shipData = await shipRes.json();
        setShippingCharge(shipData.amount || 0);
        setShippingInput(shipData.amount?.toString() || '0');
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  const handleExportCSV = async () => {
    try {
      Swal.fire({
        title: 'Exporting Products...',
        text: 'Generating CSV file...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const response = await fetch('/api/products/export');
      if (!response.ok) {
        throw new Error('Failed to export CSV');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `products_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      Swal.fire({
        icon: 'success',
        title: 'Export Successful',
        text: 'Products CSV file has been downloaded.',
        confirmButtonColor: '#be1e2d'
      });
    } catch (error) {
      console.error('Export error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Export Failed',
        text: error.message || 'Error exporting products CSV',
        confirmButtonColor: '#be1e2d'
      });
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    e.target.value = '';

    const formData = new FormData();
    formData.append('file', file);

    try {
      Swal.fire({
        title: 'Verifying CSV File...',
        text: 'Checking columns and validating product details...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const response = await fetch('/api/products/import-verify', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      Swal.close();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to verify CSV file');
      }

      setImportVerificationData(data);
      setPreviewFilterTab('ALL');
      setShowImportPreviewModal(true);
    } catch (error) {
      console.error('Import verification error:', error);
      Swal.fire({
        icon: 'error',
        title: 'CSV Verification Failed',
        text: error.message || 'Error parsing CSV file',
        confirmButtonColor: '#be1e2d'
      });
    }
  };

  const handleConfirmImportExecution = async () => {
    if (!importVerificationData || !importVerificationData.previewItems) return;

    const validItems = importVerificationData.previewItems.filter(item => item.status === 'NEW' || item.status === 'UPDATE');

    if (validItems.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'No Products To Add/Update',
        text: 'All items in this CSV are either skipped or contain errors.',
        confirmButtonColor: '#be1e2d'
      });
      return;
    }

    try {
      setImportConfirming(true);
      const response = await fetch('/api/products/import-confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: importVerificationData.previewItems })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to execute import');
      }

      setShowImportPreviewModal(false);
      setImportVerificationData(null);
      fetchData();

      Swal.fire({
        icon: 'success',
        title: 'Import Successfully Executed! 🎉',
        html: `
          <div style="text-align: left; font-size: 14px; line-height: 1.6;">
            <p style="margin: 4px 0; color: #16a34a;"><strong>New Products Added:</strong> ${result.added}</p>
            <p style="margin: 4px 0; color: #2563eb;"><strong>Products Updated (Images Preserved):</strong> ${result.updated}</p>
            <p style="margin: 4px 0; color: #64748b;"><strong>Unchanged Skipped:</strong> ${result.skipped}</p>
          </div>
        `,
        confirmButtonColor: '#be1e2d'
      });
    } catch (error) {
      console.error('Import confirmation error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Import Execution Failed',
        text: error.message || 'Error saving products to database',
        confirmButtonColor: '#be1e2d'
      });
    } finally {
      setImportConfirming(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const session = localStorage.getItem('user_session');
    if (!session) return;
    try {
      const { email } = JSON.parse(session);
      if (!email) return;
      fetch('/api/auth/profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
        .then((r) => r.ok ? r.json() : null)
        .then((data) => { if (data?.token) storeAdminToken(data.token); })
        .catch(() => {});
    } catch { /* ignore */ }
  }, []);

  const showMessage = (text, type = 'success') => {
    Swal.fire({
      title: type === 'error' ? 'Error!' : 'Success!',
      text: text,
      icon: type === 'error' ? 'error' : 'success',
      confirmButtonColor: '#be1e2d'
    });
  };

  const handleProductChange = (e) => {
    const { name, value } = e.target;
    setProductForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'name' && !prev.slugLocked) next.slug = slugify(value);
      return next;
    });
  };

  const handleAddKeyword = (formType, value) => {
    const trimmed = value.trim();
    if (!trimmed) return;

    // Split by comma in case they copy-pasted a list, or just add
    const newKeys = trimmed.split(',').map(k => k.trim()).filter(Boolean);

    if (formType === 'product') {
      const currentKeys = productForm.keywords ? productForm.keywords.split(',').map(k => k.trim()).filter(Boolean) : [];
      const updatedKeys = [...new Set([...currentKeys, ...newKeys])];
      setProductForm(prev => ({ ...prev, keywords: updatedKeys.join(', ') }));
      setProductKeywordInput('');
    } else if (formType === 'brandProduct') {
      const currentKeys = brandProductForm.keywords ? brandProductForm.keywords.split(',').map(k => k.trim()).filter(Boolean) : [];
      const updatedKeys = [...new Set([...currentKeys, ...newKeys])];
      setBrandProductForm(prev => ({ ...prev, keywords: updatedKeys.join(', ') }));
      setBrandProductKeywordInput('');
    } else if (formType === 'edit') {
      const currentKeys = editForm.keywords ? editForm.keywords.split(',').map(k => k.trim()).filter(Boolean) : [];
      const updatedKeys = [...new Set([...currentKeys, ...newKeys])];
      setEditForm(prev => ({ ...prev, keywords: updatedKeys.join(', ') }));
      setEditKeywordInput('');
    }
  };

  const handleRemoveKeyword = (formType, indexToRemove) => {
    if (formType === 'product') {
      const currentKeys = productForm.keywords ? productForm.keywords.split(',').map(k => k.trim()).filter(Boolean) : [];
      const updatedKeys = currentKeys.filter((_, idx) => idx !== indexToRemove);
      setProductForm(prev => ({ ...prev, keywords: updatedKeys.join(', ') }));
    } else if (formType === 'brandProduct') {
      const currentKeys = brandProductForm.keywords ? brandProductForm.keywords.split(',').map(k => k.trim()).filter(Boolean) : [];
      const updatedKeys = currentKeys.filter((_, idx) => idx !== indexToRemove);
      setBrandProductForm(prev => ({ ...prev, keywords: updatedKeys.join(', ') }));
    } else if (formType === 'edit') {
      const currentKeys = editForm.keywords ? editForm.keywords.split(',').map(k => k.trim()).filter(Boolean) : [];
      const updatedKeys = currentKeys.filter((_, idx) => idx !== indexToRemove);
      setEditForm(prev => ({ ...prev, keywords: updatedKeys.join(', ') }));
    }
  };

  const handleKeywordKeyDown = (e, formType) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const val = formType === 'product' ? productKeywordInput : formType === 'brandProduct' ? brandProductKeywordInput : editKeywordInput;
      handleAddKeyword(formType, val);
    }
  };

  const handleToggleRelatedProduct = (id, formType) => {
    if (formType === 'product') {
      setSelectedRelatedProducts(prev =>
        prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
      );
    } else if (formType === 'brandProduct') {
      setBrandProductSelectedRelatedProducts(prev =>
        prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
      );
    } else if (formType === 'edit') {
      setEditSelectedRelatedProducts(prev =>
        prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
      );
    }
  };

  const handleProductImageChange = (e) => {
    const files = Array.from(e.target.files);
    setProductImageError('');

    const validFiles = [];
    let hasOverLarge = false;

    files.forEach((file) => {
      if (file.size > 1024 * 1024) {
        hasOverLarge = true;
      } else {
        validFiles.push(file);
      }
    });

    if (hasOverLarge) {
      setProductImageError('Each image size must be below 1MB. Files above 1MB were skipped.');
    }

    if (validFiles.length > 0) {
      setProductImageFiles((prev) => [...prev, ...validFiles]);
      const previews = validFiles.map((file) => URL.createObjectURL(file));
      setProductImagePreviews((prev) => [...prev, ...previews]);
    }
  };

  const handleRemoveProductImage = (index) => {
    const newFiles = productImageFiles.filter((_, i) => i !== index);
    setProductImageFiles(newFiles);
    setProductImagePreviews((prev) => prev.filter((_, i) => i !== index));
    if (newFiles.length === 0) {
      setProductImageError('');
    }
  };

  const handleProductVideoChange = (e) => {
    const file = e.target.files[0];
    setProductVideoError('');
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setProductVideoError('Video size must be below 10MB.');
        setProductVideoFile(null);
        setProductVideoPreview('');
      } else {
        setProductVideoFile(file);
        setProductVideoPreview(URL.createObjectURL(file));
      }
    }
  };

  const handleRemoveProductVideo = () => {
    setProductVideoFile(null);
    setProductVideoPreview('');
    setProductVideoError('');
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    if (!productForm.name || !productForm.price || !productForm.category) {
      showMessage('Please fill all required fields for the main product.', 'error');
      return;
    }
    if (productImageFiles.length === 0) {
      showMessage('At least one product image is required.', 'error');
      return;
    }

    Swal.fire({
      title: 'Publishing Product...',
      text: 'Uploading images and configuring database entries.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    setLoading(true);

    try {
      // Submit main product with the new related product IDs
      const formData = new FormData();
      formData.append('name', productForm.name);
      formData.append('price', productForm.price);
      if (productForm.originalPrice) {
        formData.append('originalPrice', productForm.originalPrice);
      }
      if (productForm.b2bPrice) {
        formData.append('b2bPrice', productForm.b2bPrice);
      }
      formData.append('rating', productForm.rating);
      formData.append('category', productForm.category);
      if (productForm.subcategory) {
        formData.append('subcategory', productForm.subcategory);
      }
      if (productForm.brand) {
        formData.append('brand', productForm.brand);
      }
      formData.append('description', productForm.description);
      if (productForm.keywords) {
        formData.append('keywords', productForm.keywords);
      }
      if (productForm.specification) {
        formData.append('specification', productForm.specification);
      }
      formData.append('stockStatus', productForm.stockStatus);
      appendSeoToFormData(formData, productForm);
      if (productForm.quantity) {
        formData.append('quantity', productForm.quantity);
      }
      formData.append('shippingCharges', productForm.shippingCharges || '0');
      if (productForm.deliveryTime) {
        formData.append('deliveryTime', productForm.deliveryTime);
      }

      formData.append('relatedProducts', JSON.stringify(selectedRelatedProducts));

      productImageFiles.forEach((file) => {
        formData.append('images', file);
      });
      if (productVideoFile) {
        formData.append('video', productVideoFile);
      }

      const res = await fetch('/api/products', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        showMessage(`Product "${data.name}" added successfully!`);

        // Reset main product form
        setProductForm({
          name: '',
          price: '',
          originalPrice: '',
          b2bPrice: '',
          rating: '5.0',
          category: '',
          description: '',
          keywords: '',
          specification: '',
          stockStatus: 'In Stock',
          quantity: '0',
          shippingCharges: '0',
          deliveryTime: '',
          ...emptySeoFields,
        });
        setProductImageFiles([]);
        setProductImagePreviews([]);
        setProductImageError('');
        setProductVideoFile(null);
        setProductVideoPreview('');
        setProductVideoError('');
        setSelectedRelatedProducts([]);
        setProductSearchQuery('');

        fetchData();
      } else {
        showMessage(data.error || 'Failed to add main product.', 'error');
      }
    } catch (err) {
      showMessage('Server connection error.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBrandProductChange = (e) => {
    const { name, value } = e.target;
    setBrandProductForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'name' && !prev.slugLocked) next.slug = slugify(value);
      return next;
    });
  };

  const handleBrandProductImageChange = (e) => {
    const files = Array.from(e.target.files);
    setBrandProductImageError('');

    const validFiles = [];
    let hasOverLarge = false;

    files.forEach((file) => {
      if (file.size > 1024 * 1024) {
        hasOverLarge = true;
      } else {
        validFiles.push(file);
      }
    });

    if (hasOverLarge) {
      setBrandProductImageError('Each image size must be below 1MB. Files above 1MB were skipped.');
    }

    if (validFiles.length > 0) {
      setBrandProductImageFiles((prev) => [...prev, ...validFiles]);
      const previews = validFiles.map((file) => URL.createObjectURL(file));
      setBrandProductImagePreviews((prev) => [...prev, ...previews]);
    }
  };

  const handleRemoveBrandProductImage = (index) => {
    const newFiles = brandProductImageFiles.filter((_, i) => i !== index);
    setBrandProductImageFiles(newFiles);
    setBrandProductImagePreviews((prev) => prev.filter((_, i) => i !== index));
    if (newFiles.length === 0) {
      setBrandProductImageError('');
    }
  };

  const handleBrandProductVideoChange = (e) => {
    const file = e.target.files[0];
    setBrandProductVideoError('');
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setBrandProductVideoError('Video size must be below 10MB.');
        setBrandProductVideoFile(null);
        setBrandProductVideoPreview('');
      } else {
        setBrandProductVideoFile(file);
        setBrandProductVideoPreview(URL.createObjectURL(file));
      }
    }
  };

  const handleRemoveBrandProductVideo = () => {
    setBrandProductVideoFile(null);
    setBrandProductVideoPreview('');
    setBrandProductVideoError('');
  };

  const handleBrandProductSubmit = async (e) => {
    e.preventDefault();
    if (!brandProductForm.name || !brandProductForm.price || !brandProductForm.brand) {
      showMessage('Please fill all required fields.', 'error');
      return;
    }
    if (brandProductImageFiles.length === 0) {
      showMessage('At least one product image is required.', 'error');
      return;
    }

    Swal.fire({
      title: 'Publishing Brand Product...',
      text: 'Uploading images and configuring database entries.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('name', brandProductForm.name);
      formData.append('price', brandProductForm.price);
      if (brandProductForm.originalPrice) {
        formData.append('originalPrice', brandProductForm.originalPrice);
      }
      if (brandProductForm.b2bPrice) {
        formData.append('b2bPrice', brandProductForm.b2bPrice);
      }
      formData.append('rating', brandProductForm.rating);
      formData.append('brand', brandProductForm.brand);
      formData.append('description', brandProductForm.description);
      if (brandProductForm.keywords) {
        formData.append('keywords', brandProductForm.keywords);
      }
      if (brandProductForm.specification) {
        formData.append('specification', brandProductForm.specification);
      }
      formData.append('stockStatus', brandProductForm.stockStatus);
      appendSeoToFormData(formData, brandProductForm);
      if (brandProductForm.quantity) {
        formData.append('quantity', brandProductForm.quantity);
      }
      formData.append('shippingCharges', brandProductForm.shippingCharges || '0');
      if (brandProductForm.deliveryTime) {
        formData.append('deliveryTime', brandProductForm.deliveryTime);
      }

      formData.append('relatedProducts', JSON.stringify(brandProductSelectedRelatedProducts));

      brandProductImageFiles.forEach((file) => {
        formData.append('images', file);
      });
      if (brandProductVideoFile) {
        formData.append('video', brandProductVideoFile);
      }

      const res = await fetch('/api/brand-products', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        showMessage(`Brand Product "${data.name}" added successfully!`);

        // Reset brand product form
        setBrandProductForm({
          name: '',
          price: '',
          originalPrice: '',
          b2bPrice: '',
          rating: '5.0',
          brand: '',
          description: '',
          keywords: '',
          specification: '',
          stockStatus: 'In Stock',
          quantity: '0',
          shippingCharges: '0',
          deliveryTime: '',
          ...emptySeoFields,
        });
        setBrandProductImageFiles([]);
        setBrandProductImagePreviews([]);
        setBrandProductImageError('');
        setBrandProductVideoFile(null);
        setBrandProductVideoPreview('');
        setBrandProductVideoError('');
        setBrandProductSelectedRelatedProducts([]);
        setBrandProductSearchQuery('');

        fetchData();
      } else {
        showMessage(data.error || 'Failed to add brand product.', 'error');
      }
    } catch (err) {
      showMessage('Server connection error.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBrandProduct = async (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to delete this brand product from database?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#be1e2d',
      cancelButtonColor: '#718096',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await fetch(`/api/brand-products/${id}`, { method: 'DELETE' });
          if (res.ok) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Brand product has been deleted successfully.',
              icon: 'success',
              confirmButtonColor: '#be1e2d'
            });
            fetchData();
          } else {
            Swal.fire('Error!', 'Failed to delete brand product.', 'error');
          }
        } catch (err) {
          Swal.fire('Error!', 'Could not delete brand product.', 'error');
        }
      }
    });
  };

  const handleRelatedChange = (e) => {
    setRelatedForm({ ...relatedForm, [e.target.name]: e.target.value });
  };

  const handleRelatedImageChange = (e) => {
    const files = Array.from(e.target.files);
    setRelatedImageError('');

    const validFiles = [];
    let hasOverLarge = false;

    files.forEach((file) => {
      if (file.size > 1024 * 1024) {
        hasOverLarge = true;
      } else {
        validFiles.push(file);
      }
    });

    if (hasOverLarge) {
      setRelatedImageError('Each image size must be below 1MB. Files above 1MB were skipped.');
    }

    if (validFiles.length > 0) {
      setRelatedImageFiles((prev) => [...prev, ...validFiles]);
      const previews = validFiles.map((file) => URL.createObjectURL(file));
      setRelatedImagePreviews((prev) => [...prev, ...previews]);
    }
  };

  const handleRemoveRelatedImage = (index) => {
    const newFiles = relatedImageFiles.filter((_, i) => i !== index);
    setRelatedImageFiles(newFiles);
    setRelatedImagePreviews((prev) => prev.filter((_, i) => i !== index));
    if (newFiles.length === 0) {
      setRelatedImageError('');
    }
  };

  const handleRelatedVideoChange = (e) => {
    const file = e.target.files[0];
    setRelatedVideoError('');
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setRelatedVideoError('Video size must be below 10MB.');
        setRelatedVideoFile(null);
        setRelatedVideoPreview('');
      } else {
        setRelatedVideoFile(file);
        setRelatedVideoPreview(URL.createObjectURL(file));
      }
    }
  };

  const handleRemoveRelatedVideo = () => {
    setRelatedVideoFile(null);
    setRelatedVideoPreview('');
    setRelatedVideoError('');
  };

  const handleRelatedSubmit = async () => {
    if (!relatedForm.name || !relatedForm.price) {
      showMessage('Please fill all required fields for the related product.', 'error');
      return;
    }
    if (relatedImageFiles.length === 0) {
      showMessage('At least one related product image is required.', 'error');
      return;
    }

    Swal.fire({
      title: 'Publishing Related Product...',
      text: 'Uploading images to Cloudinary and writing database entry.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    setLoading(true);
    const formData = new FormData();
    formData.append('name', relatedForm.name);
    formData.append('price', relatedForm.price);
    if (relatedForm.originalPrice) {
      formData.append('originalPrice', relatedForm.originalPrice);
    }
    if (relatedForm.b2bPrice) {
      formData.append('b2bPrice', relatedForm.b2bPrice);
    }
    formData.append('rating', relatedForm.rating);
    formData.append('category', relatedForm.category);
    formData.append('description', relatedForm.description);
    formData.append('stockStatus', relatedForm.stockStatus);
    if (relatedForm.metaTitle) {
      formData.append('metaTitle', relatedForm.metaTitle);
    }
    if (relatedForm.metaDescription) {
      formData.append('metaDescription', relatedForm.metaDescription);
    }
    if (relatedForm.deliveryTime) {
      formData.append('deliveryTime', relatedForm.deliveryTime);
    }

    relatedImageFiles.forEach((file) => {
      formData.append('images', file);
    });
    if (relatedVideoFile) {
      formData.append('video', relatedVideoFile);
    }

    try {
      const res = await fetch('/api/related-products', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        showMessage(`Related Product "${data.name}" added successfully and saved to MongoDB!`);

        // Auto-link to active main product form
        if (activeTab === 'products') {
          setSelectedRelatedProducts((prev) => [...prev, data._id]);
        } else if (activeTab === 'edit') {
          setEditSelectedRelatedProducts((prev) => [...prev, data._id]);
        }

        // Fetch latest data to update dbRelatedProducts state in real-time
        await fetchData();

        setRelatedForm({
          name: '',
          price: '',
          originalPrice: '',
          b2bPrice: '',
          rating: '5.0',
          category: '',
          description: '',
          stockStatus: 'In Stock',
          metaTitle: '',
          metaDescription: '',
          deliveryTime: '',
        });
        setRelatedImageFiles([]);
        setRelatedImagePreviews([]);
        setRelatedImageError('');
        setRelatedVideoFile(null);
        setRelatedVideoPreview('');
        setRelatedVideoError('');
        fetchData();
      } else {
        showMessage(data.error || 'Failed to add related product.', 'error');
      }
    } catch (err) {
      showMessage('Server connection error.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRelatedProduct = async (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to delete this related product from database?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#be1e2d',
      cancelButtonColor: '#718096',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await fetch(`/api/related-products/${id}`, { method: 'DELETE' });
          if (res.ok) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Related product has been deleted successfully.',
              icon: 'success',
              confirmButtonColor: '#be1e2d'
            });
            fetchData();
          } else {
            Swal.fire('Error!', 'Failed to delete related product.', 'error');
          }
        } catch (err) {
          Swal.fire('Error!', 'Could not delete related product.', 'error');
        }
      }
    });
  };

  const handleCategoryBrandLogoChange = (e) => {
    const file = e.target.files[0];
    setCategoryBrandLogoError('');
    if (file) {
      if (file.size > 1024 * 1024) {
        setCategoryBrandLogoError('Logo image size must be below 1MB.');
        setCategoryBrandLogoFile(null);
        setCategoryBrandLogoPreview('');
      } else {
        setCategoryBrandLogoFile(file);
        setCategoryBrandLogoPreview(URL.createObjectURL(file));
        // Mutual exclusion: clear preset selection
        setCategoryForm(prev => ({
          ...prev,
          presetIcon: '',
          customIconSvg: ''
        }));
      }
    }
  };

  const handleRemoveCategoryBrandLogo = () => {
    setCategoryBrandLogoFile(null);
    setCategoryBrandLogoPreview('');
    setCategoryBrandLogoError('');
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!categoryForm.name) {
      showMessage('Category Name is required.', 'error');
      return;
    }

    let iconSvg = '';
    if (categoryForm.presetIcon) {
      if (categoryForm.iconType === 'preset') {
        iconSvg = PRESET_ICONS[categoryForm.presetIcon];
      } else {
        iconSvg = categoryForm.customIconSvg;
        if (iconSvg && !iconSvg.trim().startsWith('<svg')) {
          showMessage('Custom Icon must be a valid SVG string starting with <svg>.', 'error');
          return;
        }
      }
    }

    if (!iconSvg && !categoryBrandLogoFile) {
      showMessage('Please select either a Symbol Icon or upload a Category Brand Logo.', 'error');
      return;
    }

    Swal.fire({
      title: 'Creating Category...',
      text: 'Adding new category to MongoDB.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    setLoading(true);
    const formData = new FormData();
    formData.append('name', categoryForm.name);
    if (iconSvg) {
      formData.append('icon', iconSvg);
    }
    if (categoryBrandLogoFile) {
      formData.append('brandLogo', categoryBrandLogoFile);
    }
    appendSeoToFormData(formData, categoryForm);

    try {
      const res = await fetch('/api/categories', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        showMessage(`Category "${data.name}" added successfully!`);
        setCategoryForm({
          name: '',
          iconType: 'preset',
          presetIcon: 'phone',
          customIconSvg: '',
          ...emptySeoFields,
        });
        setEditingCategorySeoId('');
        setCategoryBrandLogoFile(null);
        setCategoryBrandLogoPreview('');
        setCategoryBrandLogoError('');
        fetchData();
      } else {
        showMessage(data.error || 'Failed to add category.', 'error');
      }
    } catch (err) {
      showMessage('Server connection error.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubcategorySubmit = async (e) => {
    e.preventDefault();
    if (!selectedParentCategory) {
      showMessage('Please select a parent category.', 'error');
      return;
    }
    if (!subcategoryNameInput.trim()) {
      showMessage('Please enter a subcategory name.', 'error');
      return;
    }

    Swal.fire({
      title: 'Adding Subcategory...',
      text: 'Saving subcategory to parent category in MongoDB.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    setLoading(true);
    try {
      const res = await fetch(`/api/categories/${selectedParentCategory}/subcategories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ subcategoryName: subcategoryNameInput.trim() })
      });
      const data = await res.json();
      if (res.ok) {
        showMessage(`Subcategory "${subcategoryNameInput.trim()}" added successfully!`);
        setSubcategoryNameInput('');
        fetchData();
      } else {
        showMessage(data.error || 'Failed to add subcategory.', 'error');
      }
    } catch (err) {
      showMessage('Server connection error.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubcategory = async (categoryId, subcategoryName) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete the subcategory "${subcategoryName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#be1e2d',
      cancelButtonColor: '#4a5568',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Deleting Subcategory...',
          text: 'Removing subcategory from category in database.',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });
        
        try {
          const res = await fetch(`/api/categories/${categoryId}/subcategories/${encodeURIComponent(subcategoryName)}`, {
            method: 'DELETE'
          });
          const data = await res.json();
          if (res.ok) {
            Swal.fire('Deleted!', `Subcategory "${subcategoryName}" deleted successfully.`, 'success');
            // Update the categories state
            setCategories(prev => prev.map(c => c._id === categoryId ? data : c));
          } else {
            Swal.fire('Error!', data.error || 'Failed to delete subcategory.', 'error');
          }
        } catch (err) {
          console.error(err);
          Swal.fire('Error!', 'Could not connect to database.', 'error');
        }
      }
    });
  };

  const handleBrandLogoChange = (e) => {
    const file = e.target.files[0];
    setBrandLogoError('');
    if (file) {
      if (file.size > 1024 * 1024) {
        setBrandLogoError('Logo image size must be below 1MB.');
        setBrandLogoFile(null);
        setBrandLogoPreview('');
      } else {
        setBrandLogoFile(file);
        setBrandLogoPreview(URL.createObjectURL(file));
      }
    }
  };

  const handleRemoveBrandLogo = () => {
    setBrandLogoFile(null);
    setBrandLogoPreview('');
    setBrandLogoError('');
  };

  const handleBrandSubmit = async (e) => {
    e.preventDefault();
    if (!brandForm.name) {
      showMessage('Brand Name is required.', 'error');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('name', brandForm.name);
    appendSeoToFormData(formData, brandForm);
    if (brandLogoFile) {
      formData.append('brandLogo', brandLogoFile);
    }

    try {
      const res = await fetch('/api/brands', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        showMessage(`Brand "${data.name}" added successfully!`);
        setBrandForm({
          name: '',
          ...emptySeoFields,
        });
        setEditingBrandSeoId('');
        setBrandLogoFile(null);
        setBrandLogoPreview('');
        setBrandLogoError('');
        fetchData();
      } else {
        showMessage(data.error || 'Failed to add brand.', 'error');
      }
    } catch (err) {
      showMessage('Server connection error.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBrand = async (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to delete this brand from database?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#be1e2d',
      cancelButtonColor: '#718096',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await fetch(`/api/brands/${id}`, { method: 'DELETE' });
          if (res.ok) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Brand has been deleted successfully.',
              icon: 'success',
              confirmButtonColor: '#be1e2d'
            });
            fetchData();
          } else {
            Swal.fire('Error!', 'Failed to delete brand.', 'error');
          }
        } catch (err) {
          Swal.fire('Error!', 'Could not delete brand.', 'error');
        }
      }
    });
  };

  const handleArrivalChange = (e) => {
    setArrivalForm({ ...arrivalForm, [e.target.name]: e.target.value });
  };

  const handleArrivalImageChange = (e) => {
    const file = e.target.files[0];
    setArrivalImageError('');
    if (file) {
      if (file.size > 1024 * 1024) {
        setArrivalImageError('Banner image size must be below 1MB.');
        setArrivalImageFile(null);
        setArrivalImagePreview(null);
      } else {
        setArrivalImageFile(file);
        setArrivalImagePreview(URL.createObjectURL(file));
      }
    }
  };

  const handleArrivalSubmit = async (e) => {
    e.preventDefault();
    if (!arrivalForm.title || !arrivalForm.description) {
      showMessage('Please fill out Title and Description.', 'error');
      return;
    }
    if (!arrivalImageFile) {
      showMessage('Please select an image file to upload.', 'error');
      return;
    }

    Swal.fire({
      title: 'Publishing Banner...',
      text: 'Uploading image to Cloudinary and configuring layout.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    setLoading(true);
    const formData = new FormData();
    formData.append('title', arrivalForm.title);
    formData.append('description', arrivalForm.description);
    formData.append('layoutStyle', arrivalForm.layoutStyle);
    formData.append('link', arrivalForm.link);
    formData.append('image', arrivalImageFile);

    try {
      const res = await fetch('/api/new-arrivals', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        showMessage(`New Arrival "${data.title}" published!`);
        setArrivalForm({
          title: '',
          description: '',
          layoutStyle: 'large-card',
          link: '#explore-products',
        });
        setArrivalImageFile(null);
        setArrivalImagePreview(null);
        setArrivalImageError('');
        fetchData();
      } else {
        showMessage(data.error || 'Failed to add new arrival.', 'error');
      }
    } catch (err) {
      showMessage('Server connection error.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // --- Service Handlers ---
  const handleServiceChange = (e) => {
    setServiceForm({ ...serviceForm, [e.target.name]: e.target.value });
  };

  const handleServiceImageChange = (e) => {
    const files = Array.from(e.target.files);
    setServiceImageError('');
    const validFiles = [];
    let hasOverLarge = false;

    files.forEach((file) => {
      if (file.size > 1024 * 1024) {
        hasOverLarge = true;
      } else {
        validFiles.push(file);
      }
    });

    if (hasOverLarge) {
      setServiceImageError('Each image size must be below 1MB. Files above 1MB were skipped.');
    }

    if (validFiles.length > 0) {
      setServiceImageFiles((prev) => [...prev, ...validFiles]);
      const previews = validFiles.map((file) => URL.createObjectURL(file));
      setServiceImagePreviews((prev) => [...prev, ...previews]);
    }
  };

  const handleRemoveServiceImage = (index) => {
    const newFiles = serviceImageFiles.filter((_, i) => i !== index);
    setServiceImageFiles(newFiles);
    setServiceImagePreviews((prev) => prev.filter((_, i) => i !== index));
    if (newFiles.length === 0) {
      setServiceImageError('');
    }
  };

  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    if (!serviceForm.name || !serviceForm.price || !serviceForm.brandName || !serviceForm.model || !serviceForm.fault || !serviceForm.deliveryTime) {
      showMessage('Please fill all required fields.', 'error');
      return;
    }
    if (serviceImageFiles.length === 0) {
      showMessage('At least one service image is required.', 'error');
      return;
    }

    Swal.fire({
      title: 'Publishing Service...',
      text: 'Uploading images and configuring database entries.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    setLoading(true);
    const formData = new FormData();
    formData.append('name', serviceForm.name);
    formData.append('price', serviceForm.price);
    if (serviceForm.originalPrice) {
      formData.append('originalPrice', serviceForm.originalPrice);
    }
    if (serviceForm.b2bPrice) {
      formData.append('b2bPrice', serviceForm.b2bPrice);
    }
    if (serviceForm.serviceCost) {
      formData.append('serviceCost', serviceForm.serviceCost);
    }
    if (serviceForm.shippingCharges) {
      formData.append('shippingCharges', serviceForm.shippingCharges);
    }
    formData.append('category', serviceForm.category);
    formData.append('description', serviceForm.description);
    formData.append('brandName', serviceForm.brandName);
    formData.append('model', serviceForm.model);
    formData.append('fault', serviceForm.fault);
    formData.append('deliveryTime', serviceForm.deliveryTime);
    formData.append('stockStatus', serviceForm.stockStatus);

    if (serviceBrandLogoFile) {
      formData.append('brandLogo', serviceBrandLogoFile);
    }

    serviceImageFiles.forEach((file) => {
      formData.append('images', file);
    });

    try {
      const res = await fetch('/api/services', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        showMessage(`Service "${data.name}" added successfully!`);
        setServiceForm({
          name: '',
          price: '',
          originalPrice: '',
          b2bPrice: '',
          serviceCost: '',
          shippingCharges: '0',
          category: 'Services',
          description: '',
          brandName: '',
          model: '',
          fault: '',
          deliveryTime: '',
          stockStatus: 'In Stock'
        });
        setServiceImageFiles([]);
        setServiceImagePreviews([]);
        setServiceImageError('');
        setServiceBrandLogoFile(null);
        setServiceBrandLogoPreview('');
        setServiceBrandLogoError('');
        fetchData();
      } else {
        showMessage(data.error || 'Failed to add service.', 'error');
      }
    } catch (err) {
      showMessage('Server connection error.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to delete this product from database?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#be1e2d',
      cancelButtonColor: '#718096',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await fetch(`/api/products/${id}`, { method: 'DELETE' });
          if (res.ok) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Product has been deleted successfully.',
              icon: 'success',
              confirmButtonColor: '#be1e2d'
            });
            fetchData();
          } else {
            Swal.fire('Error!', 'Failed to delete product.', 'error');
          }
        } catch (err) {
          Swal.fire('Error!', 'Could not delete product.', 'error');
        }
      }
    });
  };

  const handleDeleteService = async (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to delete this service from database?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#be1e2d',
      cancelButtonColor: '#718096',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await fetch(`/api/services/${id}`, { method: 'DELETE' });
          if (res.ok) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Service has been deleted successfully.',
              icon: 'success',
              confirmButtonColor: '#be1e2d'
            });
            fetchData();
          } else {
            Swal.fire('Error!', 'Failed to delete service.', 'error');
          }
        } catch (err) {
          Swal.fire('Error!', 'Could not delete service.', 'error');
        }
      }
    });
  };

  const handleDeleteCategory = async (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to delete this category from database?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#be1e2d',
      cancelButtonColor: '#718096',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await fetch(`/api/categories/${id}`, { method: 'DELETE' });
          if (res.ok) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Category has been deleted successfully.',
              icon: 'success',
              confirmButtonColor: '#be1e2d'
            });
            fetchData();
          } else {
            Swal.fire('Error!', 'Failed to delete category.', 'error');
          }
        } catch (err) {
          Swal.fire('Error!', 'Could not delete category.', 'error');
        }
      }
    });
  };

  const handleDeleteArrival = async (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to delete this arrival banner?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#be1e2d',
      cancelButtonColor: '#718096',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await fetch(`/api/new-arrivals/${id}`, { method: 'DELETE' });
          if (res.ok) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Arrival banner has been deleted successfully.',
              icon: 'success',
              confirmButtonColor: '#be1e2d'
            });
            fetchData();
          } else {
            Swal.fire('Error!', 'Failed to delete new arrival.', 'error');
          }
        } catch (err) {
          Swal.fire('Error!', 'Could not delete new arrival.', 'error');
        }
      }
    });
  };

  const handleUpdateOrderStatus = async (id, newStatus) => {
    try {
      const res = await fetch(`/api/orders/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        showMessage('Order status updated successfully.');
        const updated = await res.json();
        setOrders((prev) => prev.map((o) => (o._id === id ? updated : o)));
        setSelectedOrder((prev) => (prev && prev._id === id ? updated : prev));
      } else {
        const err = await res.json();
        showMessage(err.error || 'Failed to update status.', 'error');
      }
    } catch (err) {
      showMessage('Network error.', 'error');
    }
  };

  const applyUpdatedOrder = (id, updated) => {
    setOrders((prev) => prev.map((o) => (o._id === id ? updated : o)));
    setSelectedOrder((prev) => (prev && prev._id === id ? updated : prev));
  };

  const handleMarkOrderPaid = async (id) => {
    setMoneybirdBusy(true);
    try {
      const res = await fetch(`/api/orders/${id}/payment-status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentStatus: 'Paid' })
      });
      if (res.ok) {
        const updated = await res.json();
        applyUpdatedOrder(id, updated);
        showMessage(
          updated.moneybirdStatus === 'failed'
            ? 'Marked as paid. Invoice sync failed — use Retry invoice.'
            : 'Payment marked as paid.'
        );
      } else {
        const err = await res.json();
        showMessage(err.error || 'Failed to mark payment as paid.', 'error');
      }
    } catch (err) {
      showMessage('Network error.', 'error');
    } finally {
      setMoneybirdBusy(false);
    }
  };

  const handleRetryMoneybird = async (id) => {
    setMoneybirdBusy(true);
    try {
      const res = await fetch(`/api/orders/${id}/moneybird-retry`, { method: 'POST' });
      if (res.ok) {
        const updated = await res.json();
        applyUpdatedOrder(id, updated);
        showMessage(
          updated.moneybirdStatus === 'failed'
            ? (updated.moneybirdError || 'Invoice retry failed.')
            : 'Invoice sync completed.'
        );
      } else {
        const err = await res.json();
        showMessage(err.error || 'Failed to retry invoice.', 'error');
      }
    } catch (err) {
      showMessage('Network error.', 'error');
    } finally {
      setMoneybirdBusy(false);
    }
  };

  const handleDeleteOrder = async (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to delete this order from history?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#be1e2d',
      cancelButtonColor: '#718096',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await fetch(`/api/orders/${id}`, { method: 'DELETE' });
          if (res.ok) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Order has been deleted successfully.',
              icon: 'success',
              confirmButtonColor: '#be1e2d'
            });
            setOrders((prev) => prev.filter((o) => o._id !== id));
            if (selectedOrder && selectedOrder._id === id) {
              setSelectedOrder(null);
            }
          } else {
            Swal.fire('Error!', 'Failed to delete order.', 'error');
          }
        } catch (err) {
          Swal.fire('Error!', 'Could not delete order.', 'error');
        }
      }
    });
  };

  const handleStartEditBrandProduct = async (product) => {
    setEditingProduct(null);
    setActiveTab('edit');
    try {
      const res = await fetch(`/api/brand-products/${product._id || product.id}`);
      if (res.ok) {
        const fullProduct = await res.json();
        fullProduct.isBrandProduct = true;
        setEditingProduct(fullProduct);
        setEditForm({
          name: fullProduct.name || '',
          price: fullProduct.price?.toString() || '',
          originalPrice: fullProduct.originalPrice?.toString() || '',
          b2bPrice: fullProduct.b2bPrice?.toString() || '',
          rating: fullProduct.rating?.toString() || '5.0',
          category: fullProduct.category || '',
          description: fullProduct.description || '',
          keywords: Array.isArray(fullProduct.keywords) ? fullProduct.keywords.join(', ') : (fullProduct.keywords || ''),
          specification: fullProduct.specification || '',
          stockStatus: fullProduct.stockStatus || 'In Stock',
          brandName: '',
          model: '',
          fault: '',
          deliveryTime: '',
          brand: fullProduct.brand || '',
          quantity: fullProduct.quantity?.toString() || '0',
          shippingCharges: fullProduct.shippingCharges?.toString() || '0',
          ...pickSeoFromEntity(fullProduct),
        });
        const existingImgs = fullProduct.images && fullProduct.images.length > 0 ? fullProduct.images : (fullProduct.image ? [fullProduct.image] : []);
        setEditExistingImages(existingImgs);
        setEditImagePreviews([...existingImgs]);
        setEditImageFiles([]);
        setEditImageError('');

        setEditExistingVideoUrl(fullProduct.videoUrl || '');
        setEditVideoFile(null);
        setEditVideoPreview('');
        setEditVideoError('');
        const existingRelated = (fullProduct.relatedProducts || []).map((rp) => typeof rp === 'object' ? (rp._id || rp.id) : rp);
        setEditSelectedRelatedProducts(existingRelated);
      } else {
        const fallbackProduct = { ...product, isBrandProduct: true };
        setEditingProduct(fallbackProduct);
        setEditForm({
          name: product.name || '',
          price: product.price?.toString() || '',
          originalPrice: product.originalPrice?.toString() || '',
          b2bPrice: product.b2bPrice?.toString() || '',
          rating: product.rating?.toString() || '5.0',
          category: product.category || '',
          description: product.description || '',
          keywords: Array.isArray(product.keywords) ? product.keywords.join(', ') : (product.keywords || ''),
          specification: product.specification || '',
          stockStatus: product.stockStatus || 'In Stock',
          brandName: '',
          model: '',
          fault: '',
          deliveryTime: '',
          brand: product.brand || '',
          quantity: product.quantity?.toString() || '0',
          shippingCharges: product.shippingCharges?.toString() || '0',
          ...pickSeoFromEntity(product),
        });
        const existingImgs = product.images && product.images.length > 0 ? product.images : (product.image ? [product.image] : []);
        setEditExistingImages(existingImgs);
        setEditImagePreviews([...existingImgs]);
        setEditImageFiles([]);
        setEditImageError('');
        setEditExistingVideoUrl(product.videoUrl || '');
        setEditVideoFile(null);
        setEditVideoPreview('');
        setEditVideoError('');
        const existingRelated = (product.relatedProducts || []).map((rp) => typeof rp === 'object' ? (rp._id || rp.id) : rp);
        setEditSelectedRelatedProducts(existingRelated);
      }
    } catch (err) {
      console.error('Error starting brand product edit:', err);
    }
  };

  const handleStartEdit = async (product) => {
    setEditingProduct(null);
    setActiveTab('edit');
    // Fetch the fully populated product/service
    const isService = product.isService || product.brandName !== undefined || product.category === 'Services';
    const fetchUrl = isService ? `/api/services/${product._id}` : `/api/products/${product._id}`;

    try {
      const res = await fetch(fetchUrl);
      if (res.ok) {
        const fullProduct = await res.json();
        if (isService) {
          fullProduct.isService = true;
        }
        setEditingProduct(fullProduct);
        setEditForm({
          name: fullProduct.name || '',
          price: fullProduct.price?.toString() || '',
          originalPrice: fullProduct.originalPrice?.toString() || '',
          b2bPrice: fullProduct.b2bPrice?.toString() || '',
          serviceCost: fullProduct.serviceCost?.toString() || '',
          rating: fullProduct.rating?.toString() || '5.0',
          category: fullProduct.category || '',
          description: fullProduct.description || '',
          keywords: Array.isArray(fullProduct.keywords) ? fullProduct.keywords.join(', ') : (fullProduct.keywords || ''),
          specification: fullProduct.specification || '',
          stockStatus: fullProduct.stockStatus || 'In Stock',
          brandName: fullProduct.brandName || '',
          model: fullProduct.model || '',
          fault: fullProduct.fault || '',
          deliveryTime: fullProduct.deliveryTime || '',
          quantity: fullProduct.quantity?.toString() || '0',
          shippingCharges: fullProduct.shippingCharges?.toString() || '0',
          brandLogo: fullProduct.brandLogo || '',
          subcategory: fullProduct.subcategory || '',
          brand: fullProduct.brand || '',
          ...pickSeoFromEntity(fullProduct),
        });
        if (isService) {
          setEditServiceBrandLogoFile(null);
          setEditServiceBrandLogoPreview(fullProduct.brandLogo || '');
        }
        // Set existing Cloudinary images
        const existingImgs = fullProduct.images && fullProduct.images.length > 0 ? fullProduct.images : (fullProduct.image ? [fullProduct.image] : []);
        setEditExistingImages(existingImgs);
        setEditImagePreviews([...existingImgs]);
        setEditImageFiles([]);
        setEditImageError('');

        if (!isService) {
          // Set existing Cloudinary video
          setEditExistingVideoUrl(fullProduct.videoUrl || '');
          setEditVideoFile(null);
          setEditVideoPreview('');
          setEditVideoError('');
          // Extract related product IDs (relatedProducts are populated objects here)
          const existingRelated = (fullProduct.relatedProducts || []).map((rp) => typeof rp === 'object' ? (rp._id || rp.id) : rp);
          setEditSelectedRelatedProducts(existingRelated);
        }
      } else {
        // Fallback to the passed product data if API fails
        const fallbackProduct = { ...product };
        if (isService) {
          fallbackProduct.isService = true;
        }
        setEditingProduct(fallbackProduct);
        setEditForm({
          name: product.name || '',
          price: product.price?.toString() || '',
          originalPrice: product.originalPrice?.toString() || '',
          b2bPrice: product.b2bPrice?.toString() || '',
          serviceCost: product.serviceCost?.toString() || '',
          rating: product.rating?.toString() || '5.0',
          category: product.category || '',
          description: product.description || '',
          keywords: Array.isArray(product.keywords) ? product.keywords.join(', ') : (product.keywords || ''),
          specification: product.specification || '',
          stockStatus: product.stockStatus || 'In Stock',
          brandName: product.brandName || '',
          model: product.model || '',
          fault: product.fault || '',
          deliveryTime: product.deliveryTime || '',
          quantity: product.quantity?.toString() || '0',
          shippingCharges: product.shippingCharges?.toString() || '0',
          brandLogo: product.brandLogo || '',
          subcategory: product.subcategory || '',
          brand: product.brand || '',
          ...pickSeoFromEntity(product),
        });
        if (isService) {
          setEditServiceBrandLogoFile(null);
          setEditServiceBrandLogoPreview(product.brandLogo || '');
        }
        const existingImgs = product.images && product.images.length > 0 ? product.images : (product.image ? [product.image] : []);
        setEditExistingImages(existingImgs);
        setEditImagePreviews([...existingImgs]);
        setEditImageFiles([]);
        setEditImageError('');

        if (!isService) {
          setEditExistingVideoUrl(product.videoUrl || '');
          setEditVideoFile(null);
          setEditVideoPreview('');
          setEditVideoError('');
          const existingRelated = (product.relatedProducts || []).map((rp) => typeof rp === 'object' ? (rp._id || rp.id) : rp);
          setEditSelectedRelatedProducts(existingRelated);
        }
      }
    } catch (err) {
      console.error('Failed to fetch product/service for editing:', err);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'name' && !prev.slugLocked) next.slug = slugify(value);
      return next;
    });
  };

  const handleEditImageChange = (e) => {
    const files = Array.from(e.target.files);
    setEditImageError('');
    const validFiles = [];
    let hasOverLarge = false;
    files.forEach((file) => {
      if (file.size > 1024 * 1024) {
        hasOverLarge = true;
      } else {
        validFiles.push(file);
      }
    });
    if (hasOverLarge) {
      setEditImageError('Each image size must be below 1MB. Files above 1MB were skipped.');
    }
    if (validFiles.length > 0) {
      setEditImageFiles((prev) => [...prev, ...validFiles]);
      const previews = validFiles.map((file) => URL.createObjectURL(file));
      setEditImagePreviews((prev) => [...prev, ...previews]);
    }
  };

  const handleRemoveEditImage = (index) => {
    // Check if the removed preview is an existing Cloudinary URL or a new file blob
    const removedPreview = editImagePreviews[index];
    const isExistingImage = editExistingImages.includes(removedPreview);

    if (isExistingImage) {
      setEditExistingImages((prev) => prev.filter((url) => url !== removedPreview));
    } else {
      // More reliable: count how many existing images are before this index
      let existingBefore = 0;
      for (let i = 0; i < index; i++) {
        if (editExistingImages.includes(editImagePreviews[i])) existingBefore++;
      }
      const fileIdx = index - existingBefore;
      setEditImageFiles((prev) => prev.filter((_, i) => i !== fileIdx));
    }
    setEditImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEditVideoChange = (e) => {
    const file = e.target.files[0];
    setEditVideoError('');
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setEditVideoError('Video size must be below 10MB.');
        setEditVideoFile(null);
        setEditVideoPreview('');
      } else {
        setEditVideoFile(file);
        setEditVideoPreview(URL.createObjectURL(file));
        setEditExistingVideoUrl('');
      }
    }
  };

  const handleRemoveEditVideo = () => {
    setEditVideoFile(null);
    setEditVideoPreview('');
    setEditExistingVideoUrl('');
    setEditVideoError('');
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const isService = editingProduct && (editingProduct.isService || editingProduct.brandName !== undefined || editingProduct.category === 'Services');
    const isBrandProduct = editingProduct && editingProduct.isBrandProduct;

    if (!editForm.name || !editForm.price || (!isBrandProduct && !editForm.category)) {
      showMessage('Please fill all required fields.', 'error');
      return;
    }
    if (editImagePreviews.length === 0) {
      showMessage('At least one image is required.', 'error');
      return;
    }

    Swal.fire({
      title: isBrandProduct ? 'Updating Brand Product...' : isService ? 'Updating Service...' : 'Updating Product...',
      text: 'Saving changes to database and uploading new images.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    setLoading(true);
    const formData = new FormData();
    formData.append('name', editForm.name);
    formData.append('price', editForm.price);
    if (editForm.originalPrice) {
      formData.append('originalPrice', editForm.originalPrice);
    }
    if (editForm.b2bPrice) {
      formData.append('b2bPrice', editForm.b2bPrice);
    }
    if (isService && editForm.serviceCost) {
      formData.append('serviceCost', editForm.serviceCost);
    }
    formData.append('rating', editForm.rating);
    formData.append('category', editForm.category);
    formData.append('description', editForm.description);
    formData.append('stockStatus', editForm.stockStatus);
    formData.append('existingImages', JSON.stringify(editExistingImages));

      if (!isService && editForm.quantity) {
        formData.append('quantity', editForm.quantity);
      }
      if (editForm.shippingCharges) {
        formData.append('shippingCharges', editForm.shippingCharges);
      }
      if (!isService && editForm.deliveryTime) {
        formData.append('deliveryTime', editForm.deliveryTime);
      }
      if (isService) {
        formData.append('brandName', editForm.brandName);
        formData.append('model', editForm.model);
        formData.append('fault', editForm.fault);
        formData.append('deliveryTime', editForm.deliveryTime);
        formData.append('existingBrandLogo', editForm.brandLogo || '');
        if (editServiceBrandLogoFile) {
          formData.append('brandLogo', editServiceBrandLogoFile);
        }
      } else {
        if (editForm.keywords) {
          formData.append('keywords', editForm.keywords);
        }
        if (editForm.specification) {
          formData.append('specification', editForm.specification);
        }
        formData.append('existingVideoUrl', editExistingVideoUrl);
        formData.append('relatedProducts', JSON.stringify(editSelectedRelatedProducts));
        appendSeoToFormData(formData, editForm);
        if (editForm.subcategory) {
          formData.append('subcategory', editForm.subcategory);
        }
        if (editForm.brand) {
          formData.append('brand', editForm.brand);
        }
      }

      editImageFiles.forEach((file) => {
        formData.append('images', file);
      });
      if (!isService && editVideoFile) {
        formData.append('video', editVideoFile);
      }

      const updateUrl = isBrandProduct ? `/api/brand-products/${editingProduct._id}` : isService ? `/api/services/${editingProduct._id}` : `/api/products/${editingProduct._id}`;

      try {
        const res = await fetch(updateUrl, {
          method: 'PUT',
          body: formData,
        });
        const data = await res.json();
        if (res.ok) {
          showMessage(`${isBrandProduct ? 'Brand Product' : isService ? 'Service' : 'Product'} "${data.name}" updated successfully!`);
          setEditingProduct(null);
          setEditForm({
            name: '',
            price: '',
            originalPrice: '',
            b2bPrice: '',
            serviceCost: '',
            rating: '5.0',
            category: '',
            description: '',
            keywords: '',
            specification: '',
            stockStatus: 'In Stock',
            metaTitle: '',
            metaDescription: '',
            brandName: '',
            model: '',
            fault: '',
            deliveryTime: '',
            brand: '',
            quantity: '0',
            shippingCharges: '0',
          });
        setEditImageFiles([]);
        setEditImagePreviews([]);
        setEditExistingImages([]);
        setEditVideoFile(null);
        setEditVideoPreview('');
        setEditExistingVideoUrl('');
        setEditVideoError('');
        setEditSelectedRelatedProducts([]);
        setActiveTab('manage');
        fetchData();
      } else {
        showMessage(data.error || `Failed to update ${isBrandProduct ? 'brand product' : isService ? 'service' : 'product'}.`, 'error');
      }
    } catch (err) {
      showMessage('Server connection error.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleShippingSubmit = async (e) => {
    e.preventDefault();
    if (!shippingInput || isNaN(Number(shippingInput))) {
      showMessage('Please enter a valid shipping charge.', 'error');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/shipping', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ amount: parseFloat(shippingInput) })
      });
      const data = await res.json();
      if (res.ok) {
        showMessage(`Shipping charge updated to €${parseFloat(shippingInput).toFixed(2)} successfully!`);
        setShippingCharge(data.amount);
        fetchData();
      } else {
        showMessage(data.error || 'Failed to update shipping charge.', 'error');
      }
    } catch (err) {
      showMessage('Server connection error.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modern-admin-dashboard">
      {/* Side Navigation Bar */}
      <aside className="dashboard-sidebar">
        <div className="sidebar-brand">
          <div className="brand-logo-glow"></div>
          <span className="brand-name">Admin Panel</span>
        </div>

        <nav className="sidebar-nav">
          <button
            className={`nav-item-btn ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
            style={{ marginTop: '15px' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
            <span>Add Product</span>
          </button>

          <button
            className={`nav-item-btn ${activeTab === 'services' ? 'active' : ''}`}
            onClick={() => setActiveTab('services')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
            </svg>
            <span>Add Services</span>
          </button>

          <button
            className={`nav-item-btn ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 9h16M4 15h16M10 3L6 21M18 3l-4 18" />
            </svg>
            <span>Add Category</span>
          </button>

          <button
            className={`nav-item-btn ${activeTab === 'subcategories' ? 'active' : ''}`}
            onClick={() => setActiveTab('subcategories')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              <path d="M2 10h20" />
            </svg>
            <span>Add Sub Category</span>
          </button>

          <button
            className={`nav-item-btn ${activeTab === 'brands' ? 'active' : ''}`}
            onClick={() => setActiveTab('brands')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="8" r="7" />
              <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
            </svg>
            <span>Create Brand</span>
          </button>

          <button
            className={`nav-item-btn ${activeTab === 'add-brand' ? 'active' : ''}`}
            onClick={() => setActiveTab('add-brand')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="8" r="6" />
              <path d="M12 5v6M9 8h6" />
              <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
            </svg>
            <span>Add Brand</span>
          </button>

          <button
            className={`nav-item-btn ${activeTab === 'arrivals' ? 'active' : ''}`}
            onClick={() => setActiveTab('arrivals')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <span>Add New Arrival</span>
          </button>

          {activeTab === 'edit' && editingProduct && (
            <button
              className="nav-item-btn active"
              onClick={() => setActiveTab('edit')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              <span>{editingProduct.isBrandProduct ? 'Edit Brand Product' : editingProduct.isService ? 'Edit Service' : 'Edit Product'}</span>
            </button>
          )}

          <button
            className={`nav-item-btn ${activeTab === 'shipping' ? 'active' : ''}`}
            onClick={() => setActiveTab('shipping')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="3" width="15" height="13" />
              <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
              <circle cx="5.5" cy="18.5" r="2.5" />
              <circle cx="18.5" cy="18.5" r="2.5" />
            </svg>
            <span>Add Shipping</span>
          </button>

          <button
            className={`nav-item-btn ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('orders')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="9" cy="21" r="1"></circle>
              <circle cx="20" cy="21" r="1"></circle>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
            </svg>
            <span>Manage Orders</span>
          </button>

          <button
            className={`nav-item-btn manage-nav ${activeTab === 'manage' ? 'active' : ''}`}
            onClick={() => setActiveTab('manage')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <ellipse cx="12" cy="5" rx="9" ry="3" />
              <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
              <path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3" />
            </svg>
            <span>Database Manager</span>
          </button>

          <div style={{ margin: '18px 10px 8px', fontSize: '0.68rem', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', fontWeight: 600 }}>
            Content & SEO
          </div>

          <button
            className={`nav-item-btn ${activeTab === 'pages-cms' ? 'active' : ''}`}
            onClick={() => setActiveTab('pages-cms')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <span>Pages CMS</span>
          </button>

          <button
            className={`nav-item-btn ${activeTab === 'blog' ? 'active' : ''}`}
            onClick={() => setActiveTab('blog')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
            <span>Blog</span>
          </button>

          <button
            className={`nav-item-btn ${activeTab === 'landing' ? 'active' : ''}`}
            onClick={() => setActiveTab('landing')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M3 9h18M9 21V9" />
            </svg>
            <span>Landing Pages</span>
          </button>

          <button
            className={`nav-item-btn ${activeTab === 'technical-seo' ? 'active' : ''}`}
            onClick={() => setActiveTab('technical-seo')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            <span>Technical SEO</span>
          </button>
        </nav>

        <div className="sidebar-footer">
          <button
            className="exit-store-btn"
            onClick={() => {
              window.history.pushState({}, '', '/');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
            </svg>
            <span>Back to Store</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="dashboard-content">

        {/* Top Header */}
        <header className="content-header">
          <div className="header-info">
            <h1 className="header-title">Store Management</h1>
            <p className="header-subtitle">Manage catalog, categories, and new banners dynamically</p>
          </div>
        </header>

        {/* Dashboard Quick Stats */}
        <section className="dashboard-quick-stats">
          <div className="stat-card">
            <div className="stat-icon-wrapper prod-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82zM7 7h.01" />
              </svg>
            </div>
            <div className="stat-details">
              <span className="stat-value">{products.length}</span>
              <span className="stat-label">Total Products</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon-wrapper cat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 9h16M4 15h16M10 3L6 21M18 3l-4 18" />
              </svg>
            </div>
            <div className="stat-details">
              <span className="stat-value">{categories.length}</span>
              <span className="stat-label">Categories</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon-wrapper arr-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                <path d="M12 6v6l4 2" />
              </svg>
            </div>
            <div className="stat-details">
              <span className="stat-value">{arrivals.length}</span>
              <span className="stat-label">New Arrivals</span>
            </div>
          </div>

          <div className="stat-card cloud-status-card">
            <div className="stat-icon-wrapper check-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="stat-details">
              <span className="stat-value active-badge">Cloudinary</span>
              <span className="stat-label">Media Connected</span>
            </div>
          </div>
        </section>

        {loading && (
          <div className="modern-loading-screen">
            <div className="loader-orbit">
              <div className="orbit-item"></div>
              <div className="orbit-item"></div>
            </div>
            <span className="loader-text">Saving to cloud database...</span>
          </div>
        )}

        {/* Tab Panel Container */}
        <div className="panel-surface-card">

          {activeTab === 'technical-seo' && <TechnicalSeoPanel />}
          {activeTab === 'pages-cms' && <PagesCmsPanel />}
          {activeTab === 'blog' && <BlogAdminPanel />}
          {activeTab === 'landing' && <LandingPagesPanel />}

          {/* ADD PRODUCT PANEL */}
          {activeTab === 'products' && (
            <form onSubmit={handleProductSubmit} className="animate-in" style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
              <div className="layout-split-form">
                {/* Image Uploader */}
                <div className="uploader-section">
                  <h3 className="section-subtitle-minor">Product Visual</h3>

                  <div className="modern-upload-dropzone-container">
                    {productImagePreviews.length > 0 ? (
                      <div className="multiple-previews-container">
                        <div className="main-cover-preview">
                          <span className="cover-badge">Cover Image</span>
                          <img src={productImagePreviews[0]} alt="Cover preview" className="main-preview-img" />
                          <button
                            className="remove-main-image-btn"
                            onClick={() => handleRemoveProductImage(0)}
                            type="button"
                            title="Remove cover image"
                          >
                            &times;
                          </button>
                        </div>

                        <div className="thumbnails-grid">
                          {productImagePreviews.map((preview, index) => (
                            <div key={index} className={`thumbnail-card ${index === 0 ? 'active-cover' : ''}`}>
                              <img src={preview} alt={`Preview ${index + 1}`} className="thumbnail-img" />
                              <button
                                className="remove-thumbnail-btn"
                                onClick={() => handleRemoveProductImage(index)}
                                type="button"
                                title="Remove image"
                              >
                                &times;
                              </button>
                            </div>
                          ))}

                          <label htmlFor="product-image-upload" className="add-more-thumbnail-card">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="12" y1="5" x2="12" y2="19" />
                              <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                            <span>Add More</span>
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className="modern-upload-dropzone">
                        <label htmlFor="product-image-upload" className="dropzone-label">
                          <div className="dropzone-icon">
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <rect x="3" y="3" width="18" height="18" rx="2" />
                              <circle cx="8.5" cy="8.5" r="1.5" />
                              <path d="M21 15l-5-5L5 21" />
                            </svg>
                          </div>
                          <span className="dropzone-title">Upload Product Images</span>
                          <span className="dropzone-sub">Drag & drop or click to upload multiple files</span>
                        </label>
                      </div>
                    )}
                    <input
                      type="file"
                      id="product-image-upload"
                      accept="image/*"
                      onChange={handleProductImageChange}
                      multiple
                      style={{ display: 'none' }}
                    />
                    {productImageError && (
                      <div className="upload-error-line">
                        {productImageError}
                      </div>
                    )}
                  </div>

                  {/* Product Video Upload */}
                  <div className="product-video-upload-box" style={{ marginTop: '20px', marginBottom: '20px' }}>
                    <h4 className="video-section-title" style={{ fontSize: '0.9rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '10px' }}>Product Video (Optional)</h4>
                    {productVideoPreview ? (
                      <div className="video-preview-wrapper" style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid #4a5568', background: '#1a202c' }}>
                        <video src={productVideoPreview} controls style={{ width: '100%', maxHeight: '200px', display: 'block' }} />
                        <button
                          type="button"
                          className="remove-video-btn"
                          onClick={handleRemoveProductVideo}
                          style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(26, 32, 44, 0.8)', border: 'none', color: '#fff', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '5px' }}
                          title="Remove video"
                        >
                          &times; Remove Video
                        </button>
                      </div>
                    ) : (
                      <div className="modern-upload-dropzone video-zone" style={{ border: '2px dashed #4a5568', borderRadius: '8px', padding: '20px', textAlign: 'center', background: '#1a202c', transition: 'all 0.3s ease' }}>
                        <label htmlFor="product-video-upload" className="dropzone-label" style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                          <div className="dropzone-icon" style={{ color: '#a0aec0' }}>
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
                              <polygon points="12 8 18 12 12 16 12 8" />
                            </svg>
                          </div>
                          <span className="dropzone-title" style={{ fontSize: '0.85rem', fontWeight: '500', color: '#cbd5e0' }}>Upload Product Video</span>
                          <span className="dropzone-sub" style={{ fontSize: '0.75rem', color: '#718096' }}>MP4, WebM or MOV (Max 10MB)</span>
                        </label>
                      </div>
                    )}
                    <input
                      type="file"
                      id="product-video-upload"
                      accept="video/*"
                      onChange={handleProductVideoChange}
                      style={{ display: 'none' }}
                    />
                    {productVideoError && (
                      <div className="upload-error-line" style={{ color: '#e53e3e', fontSize: '0.8rem', marginTop: '5px' }}>
                        {productVideoError}
                      </div>
                    )}
                  </div>

                  <div className="mock-related-items-panel">
                    <h4 className="related-header">Live Catalog Preview</h4>
                    <div className="live-preview-widget">
                      <div className="preview-widget-header">
                        <span className="widget-meta">{productForm.category || 'Unassigned Category'}</span>
                        <div className="preview-badge-static">Cover Card</div>
                      </div>
                      <div className="preview-widget-body">
                        <h4 className="widget-name" title={productForm.name || 'Untitled New Product'}>
                          {productForm.name || 'Untitled New Product'}
                        </h4>
                        <span className="widget-price">€{productForm.price || '0.00'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-fields-section">
                  <div className="dashboard-grid-form">
                    <div className="form-group-full">
                      <label className="field-label">Product Name <span className="req">*</span></label>
                      <input
                        type="text"
                        name="name"
                        value={productForm.name}
                        onChange={handleProductChange}
                        placeholder="e.g. Premium Wireless Headset Pro"
                        required
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Store Category <span className="req">*</span></label>
                      <select
                        name="category"
                        value={productForm.category}
                        onChange={handleProductChange}
                        required
                      >
                        <option value="">Select category</option>
                        {categories.map((c) => (
                          <option key={c._id || c.name} value={c.name}>{c.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Sub Category</label>
                      <select
                        name="subcategory"
                        value={productForm.subcategory || ''}
                        onChange={handleProductChange}
                      >
                        <option value="">Select subcategory</option>
                        {categories
                          .find((cat) => cat.name === productForm.category)
                          ?.subcategories?.map((sub) => (
                            <option key={sub} value={sub}>{sub}</option>
                          ))
                        }
                      </select>
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Brand</label>
                      <select
                        name="brand"
                        value={productForm.brand || ''}
                        onChange={handleProductChange}
                      >
                        <option value="">Select brand</option>
                        {brands.map((b) => (
                          <option key={b._id || b.name} value={b.name}>{b.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Rating Value (1.0 - 5.0)</label>
                      <input
                        type="number"
                        name="rating"
                        step="0.1"
                        min="1.0"
                        max="5.0"
                        value={productForm.rating}
                        onChange={handleProductChange}
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Retail Price (EUR) <span className="req">*</span></label>
                      <input
                        type="number"
                        name="price"
                        step="0.01"
                        placeholder="299.99"
                        value={productForm.price}
                        onChange={handleProductChange}
                        required
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Original Price (Before discount) (EUR)</label>
                      <input
                        type="number"
                        name="originalPrice"
                        step="0.01"
                        placeholder="349.99"
                        value={productForm.originalPrice}
                        onChange={handleProductChange}
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">B2B Price (EUR)</label>
                      <input
                        type="number"
                        name="b2bPrice"
                        step="0.01"
                        placeholder="249.99"
                        value={productForm.b2bPrice}
                        onChange={handleProductChange}
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Stock Status <span className="req">*</span></label>
                      <select
                        name="stockStatus"
                        value={productForm.stockStatus}
                        onChange={handleProductChange}
                      >
                        <option value="In Stock">✅ In Stock</option>
                        <option value="Out of Stock">❌ Out of Stock</option>
                      </select>
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Product Quantity</label>
                      <input
                        type="number"
                        name="quantity"
                        placeholder="10"
                        value={productForm.quantity}
                        onChange={handleProductChange}
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Shipping Charges (EUR)</label>
                      <input
                        type="number"
                        name="shippingCharges"
                        step="0.01"
                        placeholder="0.00"
                        value={productForm.shippingCharges}
                        onChange={handleProductChange}
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Delivery Time</label>
                      <input
                        type="text"
                        name="deliveryTime"
                        placeholder="e.g. 3 to 4 business days"
                        value={productForm.deliveryTime}
                        onChange={handleProductChange}
                      />
                    </div>

                    <div className="form-group-full">
                      <label className="field-label">Product Description</label>
                      <textarea
                        name="description"
                        rows="4"
                        value={productForm.description}
                        onChange={handleProductChange}
                        placeholder="Enter details highlighting specifications, colors, warranty..."
                      ></textarea>
                    </div>

                    <div className="form-group-full">
                      <label className="field-label">Keywords (Type keyword and press Enter)</label>
                      <div className="keyword-badges-input-container">
                        {productForm.keywords ? productForm.keywords.split(',').map(k => k.trim()).filter(Boolean).map((kw, idx) => (
                          <span key={idx} className="keyword-badge">
                            {kw}
                            <button
                              type="button"
                              onClick={() => handleRemoveKeyword('product', idx)}
                              className="remove-keyword-btn"
                            >
                              &times;
                            </button>
                          </span>
                        )) : null}
                        <input
                          type="text"
                          placeholder={!productForm.keywords ? "e.g. iphone, 5g, discount (press Enter)" : "Add keyword..."}
                          value={productKeywordInput}
                          onChange={(e) => setProductKeywordInput(e.target.value)}
                          onKeyDown={(e) => handleKeywordKeyDown(e, 'product')}
                          onBlur={(e) => {
                            handleAddKeyword('product', e.target.value);
                          }}
                          className="keyword-input-inline"
                        />
                      </div>
                    </div>

                    <div className="form-group-full">
                      <label className="field-label">Product Specifications (One specification per line)</label>
                      <textarea
                        name="specification"
                        rows="4"
                        value={productForm.specification}
                        onChange={handleProductChange}
                        placeholder="e.g.&#10;RAM: 8GB&#10;Storage: 256GB&#10;Color: Space Grey"
                      ></textarea>
                    </div>

                    <div className="form-group-full">
                      <SeoFieldsForm
                        variant="dark"
                        title="Product SEO"
                        slugPrefix="/product/"
                        autoSlugFrom={productForm.name}
                        values={productForm}
                        onChange={(seo) => setProductForm((prev) => ({
                          ...prev,
                          ...seo,
                          metaTitle: seo.seoTitle || seo.metaTitle || prev.metaTitle,
                        }))}
                      />
                    </div>

                    {/* Associated Related Products List */}
                    <div className="form-group-full">
                      <label className="field-label">
                        Associated Related Products
                        {selectedRelatedProducts.length > 0 && (
                          <span className="related-count-badge">{selectedRelatedProducts.length} added</span>
                        )}
                      </label>
                      {selectedRelatedProducts.length === 0 ? (
                        <p className="field-helper-text" style={{ fontStyle: 'italic', color: '#a0aec0', margin: '0 0 12px 0' }}>
                          No related products associated yet. Search and select products from the catalog below.
                        </p>
                      ) : (
                        <div className="associated-products-list" style={{ marginBottom: '20px' }}>
                          {products.filter(p => selectedRelatedProducts.includes(p._id || p.id)).map((p) => (
                            <div key={p._id || p.id} className="associated-product-row">
                              <img src={p.image} alt={p.name} className="associated-thumb" />
                              <div className="associated-info">
                                <span className="associated-name" title={p.name}>{p.name}</span>
                                <span className="associated-price">€{p.price.toFixed(2)}</span>
                              </div>
                              <button
                                type="button"
                                className="btn-remove-association"
                                onClick={() => setSelectedRelatedProducts(prev => prev.filter(id => id !== (p._id || p.id)))}
                                title="Remove Link"
                              >
                                &times;
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Select Related Products */}
              <div className="create-related-section">
                <h4 className="related-sub-heading">Select Related Products</h4>
                <p className="related-sub-helper">Search and toggle products from the catalog to link as related products.</p>

                <div className="related-search-bar-wrapper" style={{ marginBottom: '15px' }}>
                  <input
                    type="text"
                    placeholder="Search products by name or category..."
                    value={productSearchQuery}
                    onChange={(e) => setProductSearchQuery(e.target.value)}
                    className="related-search-input"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '6px', border: '1px solid var(--nexus-border-dark)', background: 'var(--nexus-surface)', color: 'var(--nexus-text-main)' }}
                  />
                </div>

                <div className="related-selection-grid">
                  {products
                    .filter(p => {
                      if (!productSearchQuery) return true;
                      return (
                        p.name?.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
                        p.category?.toLowerCase().includes(productSearchQuery.toLowerCase())
                      );
                    })
                    .slice(0, productSearchQuery ? undefined : 8)
                    .map(p => {
                      const isSelected = selectedRelatedProducts.includes(p._id || p.id);
                      return (
                        <div
                          key={p._id || p.id}
                          className={`related-select-card ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleToggleRelatedProduct(p._id || p.id, 'product')}
                        >
                          <div className="select-card-img-wrapper">
                            <img src={p.image} alt={p.name} className="select-card-img" />
                          </div>
                          <div className="select-card-info">
                            <span className="select-card-name" title={p.name}>{p.name}</span>
                            <span className="select-card-price">€{p.price.toFixed(2)}</span>
                          </div>
                          <div className="select-card-indicator">
                            {isSelected ? (
                              <span className="selected-indicator-badge">✓ Selected</span>
                            ) : (
                              <span className="add-indicator-badge">+ Add</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>

              <div className="form-submit-row">
                <button type="submit" className="action-submit-btn">
                  Publish Product to Catalog
                </button>
              </div>
            </form>
          )}

          {/* ADD BRAND PRODUCT PANEL */}
          {activeTab === 'add-brand' && (
            <form onSubmit={handleBrandProductSubmit} className="animate-in" style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
              <div className="layout-split-form">
                {/* Image Uploader */}
                <div className="uploader-section">
                  <h3 className="section-subtitle-minor">Product Visual</h3>

                  <div className="modern-upload-dropzone-container">
                    {brandProductImagePreviews.length > 0 ? (
                      <div className="multiple-previews-container">
                        <div className="main-cover-preview">
                          <span className="cover-badge">Cover Image</span>
                          <img src={brandProductImagePreviews[0]} alt="Cover preview" className="main-preview-img" />
                          <button
                            className="remove-main-image-btn"
                            onClick={() => handleRemoveBrandProductImage(0)}
                            type="button"
                            title="Remove cover image"
                          >
                            &times;
                          </button>
                        </div>

                        <div className="thumbnails-grid">
                          {brandProductImagePreviews.map((preview, index) => (
                            <div key={index} className={`thumbnail-card ${index === 0 ? 'active-cover' : ''}`}>
                              <img src={preview} alt={`Preview ${index + 1}`} className="thumbnail-img" />
                              <button
                                className="remove-thumbnail-btn"
                                onClick={() => handleRemoveBrandProductImage(index)}
                                type="button"
                                title="Remove image"
                              >
                                &times;
                              </button>
                            </div>
                          ))}

                          <label htmlFor="brand-product-image-upload" className="add-more-thumbnail-card">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="12" y1="5" x2="12" y2="19" />
                              <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                            <span>Add More</span>
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className="modern-upload-dropzone">
                        <label htmlFor="brand-product-image-upload" className="dropzone-label">
                          <div className="dropzone-icon">
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <rect x="3" y="3" width="18" height="18" rx="2" />
                              <circle cx="8.5" cy="8.5" r="1.5" />
                              <path d="M21 15l-5-5L5 21" />
                            </svg>
                          </div>
                          <span className="dropzone-title">Upload Product Images</span>
                          <span className="dropzone-sub">Drag & drop or click to upload multiple files</span>
                        </label>
                      </div>
                    )}
                    <input
                      type="file"
                      id="brand-product-image-upload"
                      accept="image/*"
                      onChange={handleBrandProductImageChange}
                      multiple
                      style={{ display: 'none' }}
                    />
                    {brandProductImageError && (
                      <div className="upload-error-line">
                        {brandProductImageError}
                      </div>
                    )}
                  </div>

                  {/* Product Video Upload */}
                  <div className="product-video-upload-box" style={{ marginTop: '20px', marginBottom: '20px' }}>
                    <h4 className="video-section-title" style={{ fontSize: '0.9rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '10px' }}>Product Video (Optional)</h4>
                    {brandProductVideoPreview ? (
                      <div className="video-preview-wrapper" style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid #4a5568', background: '#1a202c' }}>
                        <video src={brandProductVideoPreview} controls style={{ width: '100%', maxHeight: '200px', display: 'block' }} />
                        <button
                          type="button"
                          className="remove-video-btn"
                          onClick={handleRemoveBrandProductVideo}
                          style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(26, 32, 44, 0.8)', border: 'none', color: '#fff', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '5px' }}
                          title="Remove video"
                        >
                          &times; Remove Video
                        </button>
                      </div>
                    ) : (
                      <div className="modern-upload-dropzone video-zone" style={{ border: '2px dashed #4a5568', borderRadius: '8px', padding: '20px', textAlign: 'center', background: '#1a202c', transition: 'all 0.3s ease' }}>
                        <label htmlFor="brand-product-video-upload" className="dropzone-label" style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                          <div className="dropzone-icon" style={{ color: '#a0aec0' }}>
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
                              <polygon points="12 8 18 12 12 16 12 8" />
                            </svg>
                          </div>
                          <span className="dropzone-title" style={{ fontSize: '0.85rem', fontWeight: '500', color: '#cbd5e0' }}>Upload Product Video</span>
                          <span className="dropzone-sub" style={{ fontSize: '0.75rem', color: '#718096' }}>MP4, WebM or MOV (Max 10MB)</span>
                        </label>
                      </div>
                    )}
                    <input
                      type="file"
                      id="brand-product-video-upload"
                      accept="video/*"
                      onChange={handleBrandProductVideoChange}
                      style={{ display: 'none' }}
                    />
                    {brandProductVideoError && (
                      <div className="upload-error-line" style={{ color: '#e53e3e', fontSize: '0.8rem', marginTop: '5px' }}>
                        {brandProductVideoError}
                      </div>
                    )}
                  </div>

                  <div className="mock-related-items-panel">
                    <h4 className="related-header">Live Brand Preview</h4>
                    <div className="live-preview-widget">
                      <div className="preview-widget-header">
                        <span className="widget-meta">{brandProductForm.brand || 'Unassigned Brand'}</span>
                        <div className="preview-badge-static">Brand Card</div>
                      </div>
                      <div className="preview-widget-body">
                        <h4 className="widget-name" title={brandProductForm.name || 'Untitled New Product'}>
                          {brandProductForm.name || 'Untitled New Product'}
                        </h4>
                        <span className="widget-price">€{brandProductForm.price || '0.00'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-fields-section">
                  <div className="dashboard-grid-form">
                    <div className="form-group-full">
                      <label className="field-label">Product Name <span className="req">*</span></label>
                      <input
                        type="text"
                        name="name"
                        value={brandProductForm.name}
                        onChange={handleBrandProductChange}
                        placeholder="e.g. Google Pixel 8 Pro"
                        required
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Select Brand <span className="req">*</span></label>
                      <select
                        name="brand"
                        value={brandProductForm.brand}
                        onChange={handleBrandProductChange}
                        required
                      >
                        <option value="">Select brand</option>
                        {brands.map((b) => (
                          <option key={b._id || b.name} value={b.name}>{b.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Rating Value (1.0 - 5.0)</label>
                      <input
                        type="number"
                        name="rating"
                        step="0.1"
                        min="1.0"
                        max="5.0"
                        value={brandProductForm.rating}
                        onChange={handleBrandProductChange}
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Retail Price (EUR) <span className="req">*</span></label>
                      <input
                        type="number"
                        name="price"
                        step="0.01"
                        placeholder="799.99"
                        value={brandProductForm.price}
                        onChange={handleBrandProductChange}
                        required
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Original Price (Before discount) (EUR)</label>
                      <input
                        type="number"
                        name="originalPrice"
                        step="0.01"
                        placeholder="899.99"
                        value={brandProductForm.originalPrice}
                        onChange={handleBrandProductChange}
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">B2B Price (EUR)</label>
                      <input
                        type="number"
                        name="b2bPrice"
                        step="0.01"
                        placeholder="699.99"
                        value={brandProductForm.b2bPrice}
                        onChange={handleBrandProductChange}
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Stock Status <span className="req">*</span></label>
                      <select
                        name="stockStatus"
                        value={brandProductForm.stockStatus}
                        onChange={handleBrandProductChange}
                      >
                        <option value="In Stock">✅ In Stock</option>
                        <option value="Out of Stock">❌ Out of Stock</option>
                      </select>
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Product Quantity</label>
                      <input
                        type="number"
                        name="quantity"
                        placeholder="10"
                        value={brandProductForm.quantity}
                        onChange={handleBrandProductChange}
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Shipping Charges (EUR)</label>
                      <input
                        type="number"
                        name="shippingCharges"
                        step="0.01"
                        placeholder="0.00"
                        value={brandProductForm.shippingCharges}
                        onChange={handleBrandProductChange}
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Delivery Time</label>
                      <input
                        type="text"
                        name="deliveryTime"
                        placeholder="e.g. 3 to 4 business days"
                        value={brandProductForm.deliveryTime}
                        onChange={handleBrandProductChange}
                      />
                    </div>

                    <div className="form-group-full">
                      <label className="field-label">Product Description</label>
                      <textarea
                        name="description"
                        rows="4"
                        value={brandProductForm.description}
                        onChange={handleBrandProductChange}
                        placeholder="Enter details highlighting specifications, colors, warranty..."
                      ></textarea>
                    </div>

                    <div className="form-group-full">
                      <label className="field-label">Keywords (Type keyword and press Enter)</label>
                      <div className="keyword-badges-input-container">
                        {brandProductForm.keywords ? brandProductForm.keywords.split(',').map(k => k.trim()).filter(Boolean).map((kw, idx) => (
                          <span key={idx} className="keyword-badge">
                            {kw}
                            <button
                              type="button"
                              onClick={() => handleRemoveKeyword('brandProduct', idx)}
                              className="remove-keyword-btn"
                            >
                              &times;
                            </button>
                          </span>
                        )) : null}
                        <input
                          type="text"
                          placeholder={!brandProductForm.keywords ? "e.g. google, pixel, discount (press Enter)" : "Add keyword..."}
                          value={brandProductKeywordInput}
                          onChange={(e) => setBrandProductKeywordInput(e.target.value)}
                          onKeyDown={(e) => handleKeywordKeyDown(e, 'brandProduct')}
                          onBlur={(e) => {
                            handleAddKeyword('brandProduct', e.target.value);
                          }}
                          className="keyword-input-inline"
                        />
                      </div>
                    </div>

                    <div className="form-group-full">
                      <label className="field-label">Product Specifications (One specification per line)</label>
                      <textarea
                        name="specification"
                        rows="4"
                        value={brandProductForm.specification}
                        onChange={handleBrandProductChange}
                        placeholder="e.g.&#10;RAM: 12GB&#10;Storage: 128GB&#10;Color: Obsidian"
                      ></textarea>
                    </div>

                    <div className="form-group-full">
                      <SeoFieldsForm
                        variant="dark"
                        title="Brand Product SEO"
                        slugPrefix="/product/"
                        autoSlugFrom={brandProductForm.name}
                        values={brandProductForm}
                        onChange={(seo) => setBrandProductForm((prev) => ({
                          ...prev,
                          ...seo,
                          metaTitle: seo.seoTitle || seo.metaTitle || prev.metaTitle,
                        }))}
                      />
                    </div>

                    {/* Associated Related Products List */}
                    <div className="form-group-full">
                      <label className="field-label">
                        Associated Related Products
                        {brandProductSelectedRelatedProducts.length > 0 && (
                          <span className="related-count-badge">{brandProductSelectedRelatedProducts.length} added</span>
                        )}
                      </label>
                      {brandProductSelectedRelatedProducts.length === 0 ? (
                        <p className="field-helper-text" style={{ fontStyle: 'italic', color: '#a0aec0', margin: '0 0 12px 0' }}>
                          No related products associated yet. Search and select products from the catalog below.
                        </p>
                      ) : (
                        <div className="associated-products-list" style={{ marginBottom: '20px' }}>
                          {brandProducts.filter(p => brandProductSelectedRelatedProducts.includes(p._id || p.id)).map((p) => (
                            <div key={p._id || p.id} className="associated-product-row">
                              <img src={p.image} alt={p.name} className="associated-thumb" />
                              <div className="associated-info">
                                <span className="associated-name" title={p.name}>{p.name}</span>
                                <span className="associated-price">€{p.price.toFixed(2)}</span>
                              </div>
                              <button
                                type="button"
                                className="btn-remove-association"
                                onClick={() => setBrandProductSelectedRelatedProducts(prev => prev.filter(id => id !== (p._id || p.id)))}
                                title="Remove Link"
                              >
                                &times; Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Select Related Products */}
              <div className="create-related-section">
                <h4 className="related-sub-heading">Select Related Products</h4>
                <p className="related-sub-helper">Search and toggle products from the catalog to link as related products.</p>

                <div className="related-search-bar-wrapper" style={{ marginBottom: '15px' }}>
                  <input
                    type="text"
                    placeholder="Search products by name or category..."
                    value={brandProductSearchQuery}
                    onChange={(e) => setBrandProductSearchQuery(e.target.value)}
                    className="related-search-input"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '6px', border: '1px solid var(--nexus-border-dark)', background: 'var(--nexus-surface)', color: 'var(--nexus-text-main)' }}
                  />
                </div>

                <div className="related-selection-grid">
                  {!brandProductForm.brand ? (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px', color: '#718096', fontSize: '14px', background: 'var(--nexus-surface)', borderRadius: '8px', border: '1px dashed var(--nexus-border)' }}>
                      Please select a brand above to load its related products.
                    </div>
                  ) : brandProducts.filter(p => {
                    const selectedBrand = brandProductForm.brand;
                    const brandLower = selectedBrand.toLowerCase();
                    const matchesBrand = p.brand && p.brand.toLowerCase().trim() === brandLower;

                    if (!matchesBrand) return false;

                    if (!brandProductSearchQuery) return true;
                    return (
                      p.name?.toLowerCase().includes(brandProductSearchQuery.toLowerCase()) ||
                      p.category?.toLowerCase().includes(brandProductSearchQuery.toLowerCase())
                    );
                  }).length === 0 ? (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px', color: '#718096', fontSize: '14px', background: 'var(--nexus-surface)', borderRadius: '8px', border: '1px dashed var(--nexus-border)' }}>
                      No products found matching the brand "{brandProductForm.brand}".
                    </div>
                  ) : brandProducts
                    .filter(p => {
                      const selectedBrand = brandProductForm.brand;
                      const brandLower = selectedBrand.toLowerCase();
                      const matchesBrand = p.brand && p.brand.toLowerCase().trim() === brandLower;

                      if (!matchesBrand) return false;

                      if (!brandProductSearchQuery) return true;
                      return (
                        p.name?.toLowerCase().includes(brandProductSearchQuery.toLowerCase()) ||
                        p.category?.toLowerCase().includes(brandProductSearchQuery.toLowerCase())
                      );
                    })
                    .slice(0, brandProductSearchQuery ? undefined : 8)
                    .map(p => {
                      const isSelected = brandProductSelectedRelatedProducts.includes(p._id || p.id);
                      return (
                        <div
                          key={p._id || p.id}
                          className={`related-select-card ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleToggleRelatedProduct(p._id || p.id, 'brandProduct')}
                        >
                          <div className="select-card-img-wrapper">
                            <img src={p.image} alt={p.name} className="select-card-img" />
                          </div>
                          <div className="select-card-info">
                            <span className="select-card-name" title={p.name}>{p.name}</span>
                            <span className="select-card-price">€{p.price.toFixed(2)}</span>
                          </div>
                          <div className="select-card-indicator">
                            {isSelected ? (
                              <span className="selected-indicator-badge">✓ Selected</span>
                            ) : (
                              <span className="add-indicator-badge">+ Add</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>

              <div className="form-submit-row">
                <button type="submit" className="action-submit-btn">
                  Publish Brand Product to Catalog
                </button>
              </div>
            </form>
          )}

          {/* ADD SERVICES PANEL */}
          {activeTab === 'services' && (
            <form onSubmit={handleServiceSubmit} className="animate-in" style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
              <div className="layout-split-form">
                {/* Image Uploader */}
                <div className="uploader-section">
                  <h3 className="section-subtitle-minor">Service Visual</h3>
                  <div className="modern-upload-dropzone-container">
                    {serviceImagePreviews.length > 0 ? (
                      <div className="multiple-previews-container">
                        <div className="main-cover-preview">
                          <span className="cover-badge">Cover Image</span>
                          <img src={serviceImagePreviews[0]} alt="Cover preview" className="main-preview-img" />
                          <button
                            className="remove-main-image-btn"
                            onClick={() => handleRemoveServiceImage(0)}
                            type="button"
                            title="Remove cover image"
                          >
                            &times;
                          </button>
                        </div>
                        <div className="thumbnails-grid">
                          {serviceImagePreviews.map((preview, index) => (
                            <div key={index} className={`thumbnail-card ${index === 0 ? 'active-cover' : ''}`}>
                              <img src={preview} alt={`Preview ${index + 1}`} className="thumbnail-img" />
                              <button
                                className="remove-thumbnail-btn"
                                onClick={() => handleRemoveServiceImage(index)}
                                type="button"
                                title="Remove image"
                              >
                                &times;
                              </button>
                            </div>
                          ))}
                          <label htmlFor="service-image-upload" className="add-more-thumbnail-card">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="12" y1="5" x2="12" y2="19" />
                              <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                            <span>Add More</span>
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className="modern-upload-dropzone">
                        <label htmlFor="service-image-upload" className="dropzone-label">
                          <div className="dropzone-icon">
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <rect x="3" y="3" width="18" height="18" rx="2" />
                              <circle cx="8.5" cy="8.5" r="1.5" />
                              <path d="M21 15l-5-5L5 21" />
                            </svg>
                          </div>
                          <span className="dropzone-title">Upload Service Images</span>
                          <span className="dropzone-sub">Drag & drop or click to upload multiple files</span>
                        </label>
                      </div>
                    )}
                    <input
                      type="file"
                      id="service-image-upload"
                      accept="image/*"
                      onChange={handleServiceImageChange}
                      multiple
                      style={{ display: 'none' }}
                    />
                     {serviceImageError && (
                      <div className="upload-error-line">
                        {serviceImageError}
                      </div>
                    )}

                    {/* Brand Logo Upload */}
                    <h3 className="section-subtitle-minor" style={{ marginTop: '24px', marginBottom: '12px' }}>Brand Logo</h3>
                    <div className="modern-upload-dropzone-container">
                      {serviceBrandLogoPreview ? (
                        <div className="main-cover-preview" style={{ height: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
                          <img src={serviceBrandLogoPreview} alt="Brand Logo Preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                          <button
                            className="remove-main-image-btn"
                            onClick={() => {
                              setServiceBrandLogoFile(null);
                              setServiceBrandLogoPreview('');
                            }}
                            type="button"
                            title="Remove brand logo"
                          >
                            &times;
                          </button>
                        </div>
                      ) : (
                        <div className="modern-upload-dropzone" style={{ padding: '20px', border: '2px dashed #cbd5e1', borderRadius: '12px', background: '#f8fafc' }}>
                          <label htmlFor="service-brand-logo-upload" className="dropzone-label" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <div className="dropzone-icon" style={{ color: '#94a3b8' }}>
                              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <rect x="3" y="3" width="18" height="18" rx="2" />
                                <circle cx="8.5" cy="8.5" r="1.5" />
                                <path d="M21 15l-5-5L5 21" />
                              </svg>
                            </div>
                            <span className="dropzone-title" style={{ fontSize: '14px', fontWeight: '600', color: '#475569' }}>Upload Brand Logo</span>
                            <span className="dropzone-sub" style={{ fontSize: '11px', color: '#94a3b8' }}>JPG, PNG or SVG (Max 1MB)</span>
                          </label>
                        </div>
                      )}
                      <input
                        type="file"
                        id="service-brand-logo-upload"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            if (file.size > 1024 * 1024) {
                              setServiceBrandLogoError('Logo size must be below 1MB.');
                            } else {
                              setServiceBrandLogoError('');
                              setServiceBrandLogoFile(file);
                              setServiceBrandLogoPreview(URL.createObjectURL(file));
                            }
                          }
                        }}
                        style={{ display: 'none' }}
                      />
                      {serviceBrandLogoError && (
                        <div className="upload-error-line">
                          {serviceBrandLogoError}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Form Fields */}
                <div className="form-fields-section">
                  <div className="dashboard-grid-form">
                    <div className="form-group-full">
                      <label className="field-label">Service Title/Name <span className="req">*</span></label>
                      <input
                        type="text"
                        name="name"
                        value={serviceForm.name}
                        onChange={handleServiceChange}
                        placeholder="e.g. iPhone 14 Pro Screen Replacement"
                        required
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Brand Name <span className="req">*</span></label>
                      <input
                        type="text"
                        name="brandName"
                        value={serviceForm.brandName}
                        onChange={handleServiceChange}
                        placeholder="e.g. Apple"
                        required
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Model <span className="req">*</span></label>
                      <input
                        type="text"
                        name="model"
                        value={serviceForm.model}
                        onChange={handleServiceChange}
                        placeholder="e.g. iPhone 14 Pro"
                        required
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Fault <span className="req">*</span></label>
                      <input
                        type="text"
                        name="fault"
                        value={serviceForm.fault}
                        onChange={handleServiceChange}
                        placeholder="e.g. Broken Display"
                        required
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Delivery Time <span className="req">*</span></label>
                      <input
                        type="text"
                        name="deliveryTime"
                        value={serviceForm.deliveryTime}
                        onChange={handleServiceChange}
                        placeholder="e.g. 30 Minutes"
                        required
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Retail Price (EUR) <span className="req">*</span></label>
                      <input
                        type="number"
                        name="price"
                        step="0.01"
                        placeholder="99.99"
                        value={serviceForm.price}
                        onChange={handleServiceChange}
                        required
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Original Price (Before discount) (EUR)</label>
                      <input
                        type="number"
                        name="originalPrice"
                        step="0.01"
                        placeholder="129.99"
                        value={serviceForm.originalPrice}
                        onChange={handleServiceChange}
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">B2B Price (EUR)</label>
                      <input
                        type="number"
                        name="b2bPrice"
                        step="0.01"
                        placeholder="79.99"
                        value={serviceForm.b2bPrice}
                        onChange={handleServiceChange}
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Service Cost (EUR)</label>
                      <input
                        type="number"
                        name="serviceCost"
                        step="0.01"
                        placeholder="50.00"
                        value={serviceForm.serviceCost}
                        onChange={handleServiceChange}
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Shipping Charges (EUR)</label>
                      <input
                        type="number"
                        name="shippingCharges"
                        step="0.01"
                        placeholder="0.00"
                        value={serviceForm.shippingCharges}
                        onChange={handleServiceChange}
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Stock Status <span className="req">*</span></label>
                      <select
                        name="stockStatus"
                        value={serviceForm.stockStatus}
                        onChange={handleServiceChange}
                      >
                        <option value="In Stock">✅ In Stock / Available</option>
                        <option value="Out of Stock">❌ Unavailable</option>
                      </select>
                    </div>

                    <div className="form-group-full">
                      <label className="field-label">Terms & Conditions</label>
                      <textarea
                        name="description"
                        rows="4"
                        value={serviceForm.description}
                        onChange={handleServiceChange}
                        placeholder="Enter terms and conditions for this repair service..."
                      ></textarea>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-submit-row">
                <button type="submit" className="action-submit-btn">
                  Publish Service to Catalog
                </button>
              </div>
            </form>
          )}

          {/* ADD CATEGORY PANEL */}
          {activeTab === 'categories' && (
            <div className="layout-split-form animate-in">
              {/* Category Logo Upload */}
              <div className="uploader-section">
                <h3 className="section-subtitle-minor">Category Brand Logo (Optional)</h3>
                <div className="modern-upload-dropzone">
                  {categoryBrandLogoPreview ? (
                    <div className="preview-container">
                      <img src={categoryBrandLogoPreview} alt="Category Brand Logo preview" className="dropzone-image-preview" />
                      <button className="remove-preview-btn" onClick={handleRemoveCategoryBrandLogo} type="button">
                        &times; Remove
                      </button>
                    </div>
                  ) : (
                    <label htmlFor="category-logo-upload" className="dropzone-label">
                      <div className="dropzone-icon">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                      </div>
                      <span className="dropzone-title">Upload Brand Logo</span>
                      <span className="dropzone-sub">Fits square layouts</span>
                    </label>
                  )}
                  <input
                    type="file"
                    id="category-logo-upload"
                    accept="image/*"
                    onChange={handleCategoryBrandLogoChange}
                    style={{ display: 'none' }}
                  />
                  {categoryBrandLogoError && (
                    <div className="upload-error-line">
                      {categoryBrandLogoError}
                    </div>
                  )}
                </div>
              </div>

              {/* Category Form Details */}
              <div className="form-fields-section">
                <form onSubmit={handleCategorySubmit} className="dashboard-grid-form">
                  <div className="form-group-full">
                    <label className="field-label">Category Label Name <span className="req">*</span></label>
                    <input
                      type="text"
                      value={categoryForm.name}
                      onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                      placeholder="e.g. Smart Wearables"
                      required
                    />
                  </div>

                  <div className="form-group-full">
                    <label className="field-label">Select Symbol Icon</label>
                    <div className="icons-preset-selector">
                      {Object.keys(PRESET_ICONS).map((key) => (
                        <div
                          key={key}
                          className={`preset-card ${categoryForm.presetIcon === key ? 'active' : ''}`}
                          onClick={() => {
                            setCategoryForm({ ...categoryForm, presetIcon: key, iconType: 'preset' });
                            handleRemoveCategoryBrandLogo();
                          }}
                        >
                          <div className="icon-badge" dangerouslySetInnerHTML={{ __html: PRESET_ICONS[key] }}></div>
                          <span className="icon-label-name">{key}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="form-group-full">
                    <label className="field-label">Edit Existing Category SEO (optional)</label>
                    <select
                      value={editingCategorySeoId}
                      onChange={(e) => {
                        const id = e.target.value;
                        setEditingCategorySeoId(id);
                        if (!id) {
                          setCategoryForm((prev) => ({ ...prev, name: '', ...emptySeoFields }));
                          return;
                        }
                        const cat = categories.find((c) => c._id === id);
                        if (cat) {
                          setCategoryForm((prev) => ({
                            ...prev,
                            name: cat.name,
                            ...pickSeoFromEntity(cat),
                          }));
                        }
                      }}
                    >
                      <option value="">— Creating new category —</option>
                      {categories.map((c) => (
                        <option key={c._id} value={c._id}>{c.name}{c.slug ? ` (/${c.slug})` : ''}</option>
                      ))}
                    </select>
                  </div>

                  <SeoFieldsForm
                    variant="dark"
                    title="Category SEO"
                    slugPrefix="/category/"
                    values={categoryForm}
                    onChange={(seo) => setCategoryForm({ ...categoryForm, ...seo })}
                  />

                  <div className="form-submit-row">
                    {editingCategorySeoId ? (
                      <button
                        type="button"
                        className="action-submit-btn"
                        onClick={async () => {
                          setLoading(true);
                          try {
                            const payload = pickSeoFromEntity(categoryForm);
                            payload.name = categoryForm.name;
                            const res = await adminFetch(`/api/categories/${editingCategorySeoId}`, {
                              method: 'PUT',
                              body: JSON.stringify(payload),
                            });
                            if (res.ok) {
                              showMessage('Category SEO updated!');
                              setEditingCategorySeoId('');
                              setCategoryForm({ name: '', iconType: 'preset', presetIcon: 'phone', customIconSvg: '', ...emptySeoFields });
                              fetchData();
                            } else {
                              const data = await res.json();
                              showMessage(data.error || 'Failed to update category SEO.', 'error');
                            }
                          } catch {
                            showMessage('Server connection error.', 'error');
                          } finally {
                            setLoading(false);
                          }
                        }}
                      >
                        Save Category SEO
                      </button>
                    ) : (
                      <button type="submit" className="action-submit-btn">
                        Create Category
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* ADD SUB CATEGORY PANEL */}
          {activeTab === 'subcategories' && (
            <div className="layout-split-form animate-in">
              <div className="form-fields-section" style={{ maxWidth: '600px', margin: '0 auto', width: '100%' }}>
                <h3 className="section-subtitle-minor">Add New Sub Category</h3>
                <form onSubmit={handleSubcategorySubmit} className="dashboard-grid-form">
                  <div className="form-group-full" style={{ marginBottom: '1.5rem' }}>
                    <label className="field-label">Select Parent Category <span className="req">*</span></label>
                    <select
                      value={selectedParentCategory}
                      onChange={(e) => setSelectedParentCategory(e.target.value)}
                      required
                      style={{
                        width: '100%',
                        padding: '0.8rem 1.2rem',
                        borderRadius: '10px',
                        border: '1px solid #e2e8f0',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.2s',
                        background: '#fff'
                      }}
                    >
                      <option value="">-- Choose a Category --</option>
                      {categories.map((cat) => (
                        <option key={cat._id} value={cat._id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group-full" style={{ marginBottom: '1.5rem' }}>
                    <label className="field-label">Sub Category Name <span className="req">*</span></label>
                    <input
                      type="text"
                      value={subcategoryNameInput}
                      onChange={(e) => setSubcategoryNameInput(e.target.value)}
                      placeholder="e.g. Chargers, Screen Protectors..."
                      required
                    />
                  </div>

                  <div className="form-submit-row">
                    <button type="submit" className="action-submit-btn">
                      Add Sub Category
                    </button>
                  </div>
                </form>

                {selectedParentCategory && (() => {
                  const parentCategoryObj = categories.find(c => c._id === selectedParentCategory);
                  const currentSubcategories = parentCategoryObj ? (parentCategoryObj.subcategories || []) : [];
                  return (
                    <div style={{ marginTop: '2.5rem', borderTop: '1px solid #e2e8f0', paddingTop: '1.5rem' }}>
                      <h4 style={{ fontSize: '1.1rem', color: '#2d3748', marginBottom: '1.2rem', fontWeight: '600' }}>
                        Existing Subcategories ({currentSubcategories.length})
                      </h4>
                      {currentSubcategories.length === 0 ? (
                        <p style={{ color: '#718096', fontSize: '0.95rem', fontStyle: 'italic' }}>
                          No subcategories added yet.
                        </p>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                          {currentSubcategories.map((sub) => (
                            <div
                              key={sub}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                background: '#f8fafc',
                                padding: '12px 18px',
                                borderRadius: '10px',
                                border: '1px solid #edf2f7',
                                transition: 'transform 0.15s ease, box-shadow 0.15s ease'
                              }}
                            >
                              <span style={{ fontSize: '0.95rem', fontWeight: '500', color: '#4a5568' }}>
                                {sub}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleDeleteSubcategory(selectedParentCategory, sub)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#be1e2d',
                                  cursor: 'pointer',
                                  padding: '4px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  transition: 'color 0.2s',
                                  outline: 'none'
                                }}
                                title="Delete subcategory"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6"></polyline>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                  <line x1="10" y1="11" x2="10" y2="17"></line>
                                  <line x1="14" y1="11" x2="14" y2="17"></line>
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {activeTab === 'brands' && (
            <div className="layout-split-form animate-in">
              {/* Brand Logo Upload */}
              <div className="uploader-section">
                <h3 className="section-subtitle-minor">Brand Logo</h3>
                <div className="modern-upload-dropzone">
                  {brandLogoPreview ? (
                    <div className="preview-container">
                      <img src={brandLogoPreview} alt="Brand Logo preview" className="dropzone-image-preview" />
                      <button className="remove-preview-btn" onClick={handleRemoveBrandLogo} type="button">
                        &times; Remove
                      </button>
                    </div>
                  ) : (
                    <label htmlFor="brand-logo-upload" className="dropzone-label">
                      <div className="dropzone-icon">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                      </div>
                      <span className="dropzone-title">Upload Brand Logo</span>
                      <span className="dropzone-sub">Fits square layouts</span>
                    </label>
                  )}
                  <input
                    type="file"
                    id="brand-logo-upload"
                    accept="image/*"
                    onChange={handleBrandLogoChange}
                    style={{ display: 'none' }}
                  />
                  {brandLogoError && (
                    <div className="upload-error-line">
                      {brandLogoError}
                    </div>
                  )}
                </div>
              </div>

              {/* Brand Form Details */}
              <div className="form-fields-section">
                <form onSubmit={handleBrandSubmit} className="dashboard-grid-form">
                  <div className="form-group-full">
                    <label className="field-label">Brand Name <span className="req">*</span></label>
                    <input
                      type="text"
                      value={brandForm.name}
                      onChange={(e) => setBrandForm({ ...brandForm, name: e.target.value })}
                      placeholder="e.g. Samsung"
                      required={!editingBrandSeoId}
                    />
                  </div>

                  <div className="form-group-full">
                    <label className="field-label">Edit Existing Brand SEO (optional)</label>
                    <select
                      value={editingBrandSeoId}
                      onChange={(e) => {
                        const id = e.target.value;
                        setEditingBrandSeoId(id);
                        if (!id) {
                          setBrandForm({ name: '', ...emptySeoFields });
                          return;
                        }
                        const brand = brands.find((b) => b._id === id);
                        if (brand) {
                          setBrandForm({ name: brand.name, ...pickSeoFromEntity(brand) });
                        }
                      }}
                    >
                      <option value="">— Creating new brand —</option>
                      {brands.map((b) => (
                        <option key={b._id} value={b._id}>{b.name}{b.slug ? ` (/${b.slug})` : ''}</option>
                      ))}
                    </select>
                  </div>

                  <SeoFieldsForm
                    variant="dark"
                    title="Brand SEO"
                    slugPrefix="/brand/"
                    values={brandForm}
                    onChange={(seo) => setBrandForm({ ...brandForm, ...seo })}
                  />

                  <div className="form-submit-row">
                    {editingBrandSeoId ? (
                      <button
                        type="button"
                        className="action-submit-btn"
                        onClick={async () => {
                          setLoading(true);
                          try {
                            const payload = pickSeoFromEntity(brandForm);
                            payload.name = brandForm.name;
                            const res = await adminFetch(`/api/brands/${editingBrandSeoId}`, {
                              method: 'PUT',
                              body: JSON.stringify(payload),
                            });
                            if (res.ok) {
                              showMessage('Brand SEO updated!');
                              setEditingBrandSeoId('');
                              setBrandForm({ name: '', ...emptySeoFields });
                              fetchData();
                            } else {
                              const data = await res.json();
                              showMessage(data.error || 'Failed to update brand SEO.', 'error');
                            }
                          } catch {
                            showMessage('Server connection error.', 'error');
                          } finally {
                            setLoading(false);
                          }
                        }}
                      >
                        Save Brand SEO
                      </button>
                    ) : (
                      <button type="submit" className="action-submit-btn">
                        Create Brand
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* ADD NEW ARRIVAL PANEL */}
          {activeTab === 'arrivals' && (
            <div className="layout-split-form animate-in">
              {/* Banner Upload */}
              <div className="uploader-section">
                <h3 className="section-subtitle-minor">Arrival Banner Visual</h3>
                <div className="modern-upload-dropzone ratio-wide">
                  {arrivalImagePreview ? (
                    <div className="preview-container">
                      <img src={arrivalImagePreview} alt="Arrival Banner preview" className="dropzone-image-preview" />
                      <button className="remove-preview-btn" onClick={() => { setArrivalImageFile(null); setArrivalImagePreview(null); }} type="button">
                        &times; Remove
                      </button>
                    </div>
                  ) : (
                    <label htmlFor="arrival-image-upload" className="dropzone-label">
                      <div className="dropzone-icon">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                      </div>
                      <span className="dropzone-title">Upload Banner Image</span>
                      <span className="dropzone-sub">Fits large layouts or square cards</span>
                    </label>
                  )}
                  <input
                    type="file"
                    id="arrival-image-upload"
                    accept="image/*"
                    onChange={handleArrivalImageChange}
                    style={{ display: 'none' }}
                  />
                  {arrivalImageError && (
                    <div className="upload-error-line">
                      {arrivalImageError}
                    </div>
                  )}
                </div>
              </div>

              {/* Form info */}
              <div className="form-fields-section">
                <form onSubmit={handleArrivalSubmit} className="dashboard-grid-form">
                  <div className="form-group-full">
                    <label className="field-label">Promotion Title <span className="req">*</span></label>
                    <input
                      type="text"
                      name="title"
                      value={arrivalForm.title}
                      onChange={handleArrivalChange}
                      placeholder="e.g. Ultra Sleek Laptops"
                      required
                    />
                  </div>

                  <div className="form-group-full">
                    <label className="field-label">Promotional Description <span className="req">*</span></label>
                    <input
                      type="text"
                      name="description"
                      value={arrivalForm.description}
                      onChange={handleArrivalChange}
                      placeholder="Sleek, powerful ultrabooks designed for productivity..."
                      required
                    />
                  </div>

                  <div className="form-group-half">
                    <label className="field-label">Layout Positioning <span className="req">*</span></label>
                    <select
                      name="layoutStyle"
                      value={arrivalForm.layoutStyle}
                      onChange={handleArrivalChange}
                      required
                    >
                      <option value="large-card">Left Large Card Slot (Vertical aspect ratio)</option>
                      <option value="horizontal-card">Top Right Wide Slot (Horizontal aspect ratio)</option>
                      <option value="square-card">Bottom Right Square Slot</option>
                    </select>
                  </div>

                  <div className="form-group-half">
                    <label className="field-label">Redirect Link</label>
                    <input
                      type="text"
                      name="link"
                      value={arrivalForm.link}
                      onChange={handleArrivalChange}
                      placeholder="#explore-products"
                    />
                  </div>

                  <div className="form-submit-row">
                    <button type="submit" className="action-submit-btn">
                      Publish Banner to Homepage
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* EDIT PRODUCT PANEL - Loading state */}
          {activeTab === 'edit' && !editingProduct && (
            <div className="animate-in" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px', flexDirection: 'column', gap: '16px' }}>
              <div style={{ width: '40px', height: '40px', border: '3px solid #4a5568', borderTopColor: '#be1e2d', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              <p style={{ color: '#a0aec0', fontSize: '0.9rem' }}>Loading product data...</p>
            </div>
          )}

          {/* EDIT PRODUCT PANEL */}
          {activeTab === 'edit' && editingProduct && (
            <div className="animate-in" style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
              <div className="layout-split-form">
                {/* Image Uploader */}
                <div className="uploader-section">
                  <h3 className="section-subtitle-minor">
                    <span className="edit-badge-header">Editing</span> Product Images
                  </h3>

                  <div className="modern-upload-dropzone-container">
                    {editImagePreviews.length > 0 ? (
                      <div className="multiple-previews-container">
                        <div className="main-cover-preview">
                          <span className="cover-badge">Cover Image</span>
                          <img src={editImagePreviews[0]} alt="Cover preview" className="main-preview-img" />
                          <button
                            className="remove-main-image-btn"
                            onClick={() => handleRemoveEditImage(0)}
                            type="button"
                            title="Remove cover image"
                          >
                            &times;
                          </button>
                        </div>

                        <div className="thumbnails-grid">
                          {editImagePreviews.map((preview, index) => (
                            <div key={index} className={`thumbnail-card ${index === 0 ? 'active-cover' : ''}`}>
                              <img src={preview} alt={`Preview ${index + 1}`} className="thumbnail-img" />
                              <button
                                className="remove-thumbnail-btn"
                                onClick={() => handleRemoveEditImage(index)}
                                type="button"
                                title="Remove image"
                              >
                                &times;
                              </button>
                              {editExistingImages.includes(preview) && (
                                <span className="existing-image-badge">Saved</span>
                              )}
                            </div>
                          ))}

                          <label htmlFor="edit-image-upload" className="add-more-thumbnail-card">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="12" y1="5" x2="12" y2="19" />
                              <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                            <span>Add More</span>
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className="modern-upload-dropzone">
                        <label htmlFor="edit-image-upload" className="dropzone-label">
                          <div className="dropzone-icon">
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <rect x="3" y="3" width="18" height="18" rx="2" />
                              <circle cx="8.5" cy="8.5" r="1.5" />
                              <path d="M21 15l-5-5L5 21" />
                            </svg>
                          </div>
                          <span className="dropzone-title">Upload Product Images</span>
                          <span className="dropzone-sub">Drag & drop or click to upload multiple files</span>
                        </label>
                      </div>
                    )}
                    <input
                      type="file"
                      id="edit-image-upload"
                      accept="image/*"
                      onChange={handleEditImageChange}
                      multiple
                      style={{ display: 'none' }}
                    />
                    {editImageError && (
                      <div className="upload-error-line">
                        {editImageError}
                      </div>
                    )}
                  </div>

                  {/* Edit Product Video Upload */}
                  {!editingProduct.isService && (
                    <div className="product-video-upload-box" style={{ marginTop: '20px', marginBottom: '20px' }}>
                      <h4 className="video-section-title" style={{ fontSize: '0.9rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '10px' }}>Product Video (Optional)</h4>
                      {editVideoPreview || editExistingVideoUrl ? (
                        <div className="video-preview-wrapper" style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid #4a5568', background: '#1a202c' }}>
                          <video src={editVideoPreview || editExistingVideoUrl} controls style={{ width: '100%', maxHeight: '200px', display: 'block' }} />
                          <button
                            type="button"
                            className="remove-video-btn"
                            onClick={handleRemoveEditVideo}
                            style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(26, 32, 44, 0.8)', border: 'none', color: '#fff', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '5px' }}
                            title="Remove video"
                          >
                            &times; Remove Video
                          </button>
                          {editExistingVideoUrl && (
                            <span className="existing-image-badge" style={{ position: 'absolute', bottom: '10px', left: '10px', background: '#be1e2d', color: '#fff', padding: '2px 8px', borderRadius: '3px', fontSize: '0.7rem' }}>Saved Video</span>
                          )}
                        </div>
                      ) : (
                        <div className="modern-upload-dropzone video-zone" style={{ border: '2px dashed #4a5568', borderRadius: '8px', padding: '20px', textAlign: 'center', background: '#1a202c', transition: 'all 0.3s ease' }}>
                          <label htmlFor="edit-video-upload" className="dropzone-label" style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                            <div className="dropzone-icon" style={{ color: '#a0aec0' }}>
                              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
                                <polygon points="12 8 18 12 12 16 12 8" />
                              </svg>
                            </div>
                            <span className="dropzone-title" style={{ fontSize: '0.85rem', fontWeight: '500', color: '#cbd5e0' }}>Upload New Video</span>
                            <span className="dropzone-sub" style={{ fontSize: '0.75rem', color: '#718096' }}>MP4, WebM or MOV (Max 10MB)</span>
                          </label>
                        </div>
                      )}
                      <input
                        type="file"
                        id="edit-video-upload"
                        accept="video/*"
                        onChange={handleEditVideoChange}
                        style={{ display: 'none' }}
                      />
                      {editVideoError && (
                        <div className="upload-error-line" style={{ color: '#e53e3e', fontSize: '0.8rem', marginTop: '5px' }}>
                          {editVideoError}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Edit Service Brand Logo Upload */}
                  {editingProduct.isService && (
                    <div className="product-video-upload-box" style={{ marginTop: '20px', marginBottom: '20px' }}>
                      <h4 className="video-section-title" style={{ fontSize: '0.9rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '10px' }}>Brand Logo (Optional)</h4>
                      {editServiceBrandLogoPreview || editForm.brandLogo ? (
                        <div className="video-preview-wrapper" style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid #4a5568', background: '#1a202c', height: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <img src={editServiceBrandLogoPreview || editForm.brandLogo} alt="Brand Logo Preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                          <button
                            type="button"
                            className="remove-video-btn"
                            onClick={() => {
                              setEditServiceBrandLogoFile(null);
                              setEditServiceBrandLogoPreview('');
                              setEditForm(prev => ({ ...prev, brandLogo: '' }));
                            }}
                            style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(26, 32, 44, 0.8)', border: 'none', color: '#fff', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '5px' }}
                            title="Remove brand logo"
                          >
                            &times; Remove
                          </button>
                        </div>
                      ) : (
                        <div className="modern-upload-dropzone video-zone" style={{ border: '2px dashed #4a5568', borderRadius: '8px', padding: '20px', textAlign: 'center', background: '#1a202c' }}>
                          <label htmlFor="edit-brand-logo-upload" className="dropzone-label" style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                            <div className="dropzone-icon" style={{ color: '#a0aec0' }}>
                              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <rect x="3" y="3" width="18" height="18" rx="2" />
                                <circle cx="8.5" cy="8.5" r="1.5" />
                                <path d="M21 15l-5-5L5 21" />
                              </svg>
                            </div>
                            <span className="dropzone-title" style={{ fontSize: '0.85rem', fontWeight: '500', color: '#cbd5e0' }}>Upload New Brand Logo</span>
                          </label>
                        </div>
                      )}
                      <input
                        type="file"
                        id="edit-brand-logo-upload"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            if (file.size > 1024 * 1024) {
                              showMessage('Brand logo size must be below 1MB.', 'error');
                            } else {
                              setEditServiceBrandLogoFile(file);
                              setEditServiceBrandLogoPreview(URL.createObjectURL(file));
                            }
                          }
                        }}
                        style={{ display: 'none' }}
                      />
                    </div>
                  )}

                  <div className="mock-related-items-panel">
                    <h4 className="related-header">Editing Preview</h4>
                    <div className="live-preview-widget edit-mode-widget">
                      <div className="preview-widget-header">
                        <span className="widget-meta">{editForm.brand || editForm.category || 'Unassigned Category'}</span>
                        <div className="preview-badge-static edit-badge">Editing</div>
                      </div>
                      <div className="preview-widget-body">
                        <h4 className="widget-name" title={editForm.name || 'Untitled Product'}>
                          {editForm.name || 'Untitled Product'}
                        </h4>
                        <span className="widget-price">€{editForm.price || '0.00'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form details */}
                <div className="form-fields-section">
                  <div className="edit-form-header">
                    <h3 className="section-panel-title">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                      Edit: {editingProduct.name}
                    </h3>
                    <button
                      className="cancel-edit-btn"
                      onClick={() => { setActiveTab('manage'); setEditingProduct(null); }}
                      type="button"
                    >
                      Cancel
                    </button>
                  </div>

                  <form onSubmit={handleEditSubmit} className="dashboard-grid-form">
                    <div className="form-group-full">
                      <label className="field-label">Product Name <span className="req">*</span></label>
                      <input
                        type="text"
                        name="name"
                        value={editForm.name}
                        onChange={handleEditChange}
                        placeholder="e.g. Premium Wireless Headset Pro"
                        required
                      />
                    </div>

                    {editingProduct.isBrandProduct ? (
                      <div className="form-group-half">
                        <label className="field-label">Select Brand <span className="req">*</span></label>
                        <select
                          name="brand"
                          value={editForm.brand || ''}
                          onChange={handleEditChange}
                          required
                        >
                          <option value="">Choose a brand</option>
                          {brands.map((b) => (
                            <option key={b._id || b.name} value={b.name}>{b.name}</option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <>
                        <div className="form-group-half">
                          <label className="field-label">Store Category <span className="req">*</span></label>
                          <select
                            name="category"
                            value={editForm.category}
                            onChange={handleEditChange}
                            required
                          >
                            <option value="">Select category</option>
                            {categories.map((c) => (
                              <option key={c._id || c.name} value={c.name}>{c.name}</option>
                            ))}
                          </select>
                        </div>

                        {!editingProduct.isService && (
                          <>
                            <div className="form-group-half">
                              <label className="field-label">Sub Category</label>
                              <select
                                name="subcategory"
                                value={editForm.subcategory || ''}
                                onChange={handleEditChange}
                              >
                                <option value="">Select subcategory</option>
                                {categories
                                  .find((cat) => cat.name === editForm.category)
                                  ?.subcategories?.map((sub) => (
                                    <option key={sub} value={sub}>{sub}</option>
                                  ))
                                }
                              </select>
                            </div>

                            <div className="form-group-half">
                              <label className="field-label">Brand</label>
                              <select
                                name="brand"
                                value={editForm.brand || ''}
                                onChange={handleEditChange}
                              >
                                <option value="">Select brand</option>
                                {brands.map((b) => (
                                  <option key={b._id || b.name} value={b.name}>{b.name}</option>
                                ))}
                              </select>
                            </div>
                          </>
                        )}
                      </>
                    )}

                    <div className="form-group-half">
                      <label className="field-label">Rating Value (1.0 - 5.0)</label>
                      <input
                        type="number"
                        name="rating"
                        step="0.1"
                        min="1.0"
                        max="5.0"
                        value={editForm.rating}
                        onChange={handleEditChange}
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Retail Price (EUR) <span className="req">*</span></label>
                      <input
                        type="number"
                        name="price"
                        step="0.01"
                        placeholder="299.99"
                        value={editForm.price}
                        onChange={handleEditChange}
                        required
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">Original Price (Before discount) (EUR)</label>
                      <input
                        type="number"
                        name="originalPrice"
                        step="0.01"
                        placeholder="349.99"
                        value={editForm.originalPrice}
                        onChange={handleEditChange}
                      />
                    </div>

                    <div className="form-group-half">
                      <label className="field-label">B2B Price (EUR)</label>
                      <input
                        type="number"
                        name="b2bPrice"
                        step="0.01"
                        placeholder="249.99"
                        value={editForm.b2bPrice}
                        onChange={handleEditChange}
                      />
                    </div>

                    {editingProduct.isService && (
                      <>
                        <div className="form-group-half">
                          <label className="field-label">Service Cost (EUR)</label>
                          <input
                            type="number"
                            name="serviceCost"
                            step="0.01"
                            placeholder="50.00"
                            value={editForm.serviceCost}
                            onChange={handleEditChange}
                          />
                        </div>
                        <div className="form-group-half">
                          <label className="field-label">Shipping Charges (EUR)</label>
                          <input
                            type="number"
                            name="shippingCharges"
                            step="0.01"
                            placeholder="0.00"
                            value={editForm.shippingCharges || '0'}
                            onChange={handleEditChange}
                          />
                        </div>
                      </>
                    )}

                    <div className="form-group-half">
                      <label className="field-label">Stock Status <span className="req">*</span></label>
                      <select
                        name="stockStatus"
                        value={editForm.stockStatus}
                        onChange={handleEditChange}
                      >
                        <option value="In Stock">✅ In Stock</option>
                        <option value="Out of Stock">❌ Out of Stock</option>
                      </select>
                    </div>

                    <div className="form-group-full">
                      <label className="field-label">Product Description</label>
                      <textarea
                        name="description"
                        rows="4"
                        value={editForm.description}
                        onChange={handleEditChange}
                        placeholder="Enter details highlighting specifications, colors, warranty..."
                      ></textarea>
                    </div>

                    {editingProduct.isService ? (
                      <>
                        <div className="form-group-half">
                          <label className="field-label">Brand Name <span className="req">*</span></label>
                          <input
                            type="text"
                            name="brandName"
                            value={editForm.brandName}
                            onChange={handleEditChange}
                            placeholder="e.g. Apple"
                            required
                          />
                        </div>

                        <div className="form-group-half">
                          <label className="field-label">Model <span className="req">*</span></label>
                          <input
                            type="text"
                            name="model"
                            value={editForm.model}
                            onChange={handleEditChange}
                            placeholder="e.g. iPhone 14 Pro"
                            required
                          />
                        </div>

                        <div className="form-group-half">
                          <label className="field-label">Fault <span className="req">*</span></label>
                          <input
                            type="text"
                            name="fault"
                            value={editForm.fault}
                            onChange={handleEditChange}
                            placeholder="e.g. Broken Display"
                            required
                          />
                        </div>

                        <div className="form-group-half">
                          <label className="field-label">Delivery Time <span className="req">*</span></label>
                          <input
                            type="text"
                            name="deliveryTime"
                            value={editForm.deliveryTime}
                            onChange={handleEditChange}
                            placeholder="e.g. 1-2 Hours"
                            required
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="form-group-half">
                          <label className="field-label">Product Quantity</label>
                          <input
                            type="number"
                            name="quantity"
                            placeholder="10"
                            value={editForm.quantity || '0'}
                            onChange={handleEditChange}
                          />
                        </div>

                        <div className="form-group-half">
                          <label className="field-label">Shipping Charges (EUR)</label>
                          <input
                            type="number"
                            name="shippingCharges"
                            step="0.01"
                            placeholder="0.00"
                            value={editForm.shippingCharges || '0'}
                            onChange={handleEditChange}
                          />
                        </div>

                        <div className="form-group-half">
                          <label className="field-label">Delivery Time</label>
                          <input
                            type="text"
                            name="deliveryTime"
                            placeholder="e.g. 3 to 4 business days"
                            value={editForm.deliveryTime || ''}
                            onChange={handleEditChange}
                          />
                        </div>

                        <div className="form-group-full">
                          <label className="field-label">Keywords (Type keyword and press Enter)</label>
                          <div className="keyword-badges-input-container">
                            {editForm.keywords ? editForm.keywords.split(',').map(k => k.trim()).filter(Boolean).map((kw, idx) => (
                              <span key={idx} className="keyword-badge">
                                {kw}
                                <button
                                  type="button"
                                  onClick={() => handleRemoveKeyword('edit', idx)}
                                  className="remove-keyword-btn"
                                >
                                  &times;
                                </button>
                              </span>
                            )) : null}
                            <input
                              type="text"
                              placeholder={!editForm.keywords ? "e.g. iphone, 5g, discount (press Enter)" : "Add keyword..."}
                              value={editKeywordInput}
                              onChange={(e) => setEditKeywordInput(e.target.value)}
                              onKeyDown={(e) => handleKeywordKeyDown(e, 'edit')}
                              onBlur={(e) => {
                                handleAddKeyword('edit', e.target.value);
                              }}
                              className="keyword-input-inline"
                            />
                          </div>
                        </div>

                        <div className="form-group-full">
                          <label className="field-label">Product Specifications (One specification per line)</label>
                          <textarea
                            name="specification"
                            rows="4"
                            value={editForm.specification}
                            onChange={handleEditChange}
                            placeholder="e.g.&#10;RAM: 8GB&#10;Storage: 256GB&#10;Color: Space Grey"
                          ></textarea>
                        </div>
                      </>
                    )}

                    {!editingProduct?.isService && (
                      <div className="form-group-full">
                        <SeoFieldsForm
                          variant="dark"
                          title="Product SEO"
                          slugPrefix="/product/"
                          autoSlugFrom={editForm.name}
                          values={editForm}
                          onChange={(seo) => setEditForm((prev) => ({
                            ...prev,
                            ...seo,
                            metaTitle: seo.seoTitle || seo.metaTitle || prev.metaTitle,
                          }))}
                        />
                      </div>
                    )}

                    {/* Associated Related Products List */}
                    {!editingProduct.isService && (
                      <div className="form-group-full">
                        <label className="field-label">
                          Associated Related Products
                          {editSelectedRelatedProducts.length > 0 && (
                            <span className="related-count-badge">{editSelectedRelatedProducts.length} added</span>
                          )}
                        </label>
                        {editSelectedRelatedProducts.length === 0 ? (
                          <p className="field-helper-text" style={{ fontStyle: 'italic', color: '#a0aec0', margin: '0 0 12px 0' }}>
                            No related products associated yet. Search and select products from the catalog below.
                          </p>
                        ) : (
                          <div className="associated-products-list" style={{ marginBottom: '20px' }}>
                            {(editingProduct.isBrandProduct ? brandProducts : products).filter(p => editSelectedRelatedProducts.includes(p._id || p.id)).map((p) => (
                              <div key={p._id || p.id} className="associated-product-row">
                                <img src={p.image} alt={p.name} className="associated-thumb" />
                                <div className="associated-info">
                                  <span className="associated-name" title={p.name}>{p.name}</span>
                                  <span className="associated-price">€{p.price.toFixed(2)}</span>
                                </div>
                                <button
                                  type="button"
                                  className="btn-remove-association"
                                  onClick={() => setEditSelectedRelatedProducts(prev => prev.filter(id => id !== (p._id || p.id)))}
                                  title="Remove Link"
                                >
                                  &times;
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="form-submit-row edit-submit-row">
                      <button
                        type="button"
                        className="action-cancel-btn"
                        onClick={() => { setActiveTab('manage'); setEditingProduct(null); }}
                      >
                        Discard Changes
                      </button>
                      <button type="submit" className="action-submit-btn edit-submit-btn">
                        💾 Update {editingProduct.isService ? 'Service' : 'Product'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {/* Select Related Products for Edit */}
              {!editingProduct.isService && (
                <div className="create-related-section">
                  <h4 className="related-sub-heading">Select Related Products</h4>
                  <p className="related-sub-helper">Search and toggle products from the catalog to link as related products.</p>

                  <div className="related-search-bar-wrapper" style={{ marginBottom: '15px' }}>
                    <input
                      type="text"
                      placeholder="Search products by name or category..."
                      value={editProductSearchQuery}
                      onChange={(e) => setEditProductSearchQuery(e.target.value)}
                      className="related-search-input"
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '6px', border: '1px solid var(--nexus-border-dark)', background: 'var(--nexus-surface)', color: 'var(--nexus-text-main)' }}
                    />
                  </div>

                  <div className="related-selection-grid">
                    {editingProduct.isBrandProduct ? (
                      !editForm.brand ? (
                        <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px', color: '#718096', fontSize: '14px', background: 'var(--nexus-surface)', borderRadius: '8px', border: '1px dashed var(--nexus-border)' }}>
                          Please select a brand above to load its related products.
                        </div>
                      ) : brandProducts.filter(p => {
                        const selectedBrand = editForm.brand;
                        const brandLower = selectedBrand.toLowerCase();
                        const matchesBrand = p.brand && p.brand.toLowerCase().trim() === brandLower;

                        if (!matchesBrand) return false;

                        if (!editProductSearchQuery) return true;
                        return (
                          p.name?.toLowerCase().includes(editProductSearchQuery.toLowerCase()) ||
                          p.category?.toLowerCase().includes(editProductSearchQuery.toLowerCase())
                        );
                      }).length === 0 ? (
                        <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px', color: '#718096', fontSize: '14px', background: 'var(--nexus-surface)', borderRadius: '8px', border: '1px dashed var(--nexus-border)' }}>
                          No products found matching the brand "{editForm.brand}".
                        </div>
                      ) : brandProducts
                        .filter(p => {
                          const selectedBrand = editForm.brand;
                          const brandLower = selectedBrand.toLowerCase();
                          const matchesBrand = p.brand && p.brand.toLowerCase().trim() === brandLower;

                          if (!matchesBrand) return false;

                          if (!editProductSearchQuery) return true;
                          return (
                            p.name?.toLowerCase().includes(editProductSearchQuery.toLowerCase()) ||
                            p.category?.toLowerCase().includes(editProductSearchQuery.toLowerCase())
                          );
                        })
                        .slice(0, editProductSearchQuery ? undefined : 8)
                        .map(p => {
                          const isSelected = editSelectedRelatedProducts.includes(p._id || p.id);
                          return (
                            <div
                              key={p._id || p.id}
                              className={`related-select-card ${isSelected ? 'selected' : ''}`}
                              onClick={() => handleToggleRelatedProduct(p._id || p.id, 'edit')}
                            >
                              <div className="select-card-img-wrapper">
                                <img src={p.image} alt={p.name} className="select-card-img" />
                              </div>
                              <div className="select-card-info">
                                <span className="select-card-name" title={p.name}>{p.name}</span>
                                <span className="select-card-price">€{p.price.toFixed(2)}</span>
                              </div>
                              <div className="select-card-indicator">
                                {isSelected ? (
                                  <span className="selected-indicator-badge">✓ Selected</span>
                                ) : (
                                  <span className="add-indicator-badge">+ Add</span>
                                )}
                              </div>
                            </div>
                          );
                        })
                    ) : (
                      [...products, ...brandProducts]
                        .filter(p => p._id !== editingProduct._id && p.id !== editingProduct._id)
                        .filter(p => {
                          if (!editProductSearchQuery) return true;
                          return (
                            p.name?.toLowerCase().includes(editProductSearchQuery.toLowerCase()) ||
                            p.category?.toLowerCase().includes(editProductSearchQuery.toLowerCase())
                          );
                        })
                        .slice(0, editProductSearchQuery ? undefined : 8)
                        .map(p => {
                          const isSelected = editSelectedRelatedProducts.includes(p._id || p.id);
                          return (
                            <div
                              key={p._id || p.id}
                              className={`related-select-card ${isSelected ? 'selected' : ''}`}
                              onClick={() => handleToggleRelatedProduct(p._id || p.id, 'edit')}
                            >
                              <div className="select-card-img-wrapper">
                                <img src={p.image} alt={p.name} className="select-card-img" />
                              </div>
                              <div className="select-card-info">
                                <span className="select-card-name" title={p.name}>{p.name}</span>
                                <span className="select-card-price">€{p.price.toFixed(2)}</span>
                              </div>
                              <div className="select-card-indicator">
                                {isSelected ? (
                                  <span className="selected-indicator-badge">✓ Selected</span>
                                ) : (
                                  <span className="add-indicator-badge">+ Add</span>
                                )}
                              </div>
                            </div>
                          );
                        })
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* DATABASE INVENTORY MANAGER */}
          {activeTab === 'manage' && (() => {
            const query = adminSearchQuery.toLowerCase().trim();
            const filteredProducts = products.filter(p => 
              p.name?.toLowerCase().includes(query) || 
              p.category?.toLowerCase().includes(query) ||
              (p.brandName && p.brandName.toLowerCase().includes(query)) ||
              (p.model && p.model.toLowerCase().includes(query)) ||
              (p.fault && p.fault.toLowerCase().includes(query))
            );
            const filteredBrandProducts = brandProducts.filter(p => 
              p.name?.toLowerCase().includes(query) || 
              p.brand?.toLowerCase().includes(query)
            );
            const filteredServices = services.filter(s => 
              s.name?.toLowerCase().includes(query) || 
              s.brandName?.toLowerCase().includes(query) || 
              s.model?.toLowerCase().includes(query) || 
              s.fault?.toLowerCase().includes(query) ||
              s.description?.toLowerCase().includes(query)
            );
            const filteredDbRelatedProducts = dbRelatedProducts.filter(p => 
              p.name?.toLowerCase().includes(query) || 
              p.category?.toLowerCase().includes(query)
            );
            const filteredCategories = categories.filter(c => 
              c.name?.toLowerCase().includes(query)
            );
            const filteredBrands = brands.filter(b => 
              b.name?.toLowerCase().includes(query)
            );
            const filteredArrivals = arrivals.filter(a => 
              a.title?.toLowerCase().includes(query) || 
              a.description?.toLowerCase().includes(query)
            );

            return (
              <div className="database-manager-layout animate-in">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px', marginBottom: '25px', borderBottom: '1px solid var(--nexus-border)', paddingBottom: '20px' }}>
                  <div style={{ flex: '1', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <h3 className="section-panel-title" style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>Active Database Catalog</h3>

                  </div>
                  <div className="admin-search-bar-container" style={{ position: 'relative', width: '100%', maxWidth: '400px' }}>
                    <input
                      type="text"
                      placeholder="Search products, services, brands, categories..."
                      value={adminSearchQuery}
                      onChange={(e) => setAdminSearchQuery(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px 16px 12px 42px',
                        borderRadius: '10px',
                        border: '1.5px solid #cbd5e1',
                        fontSize: '14px',
                        outline: 'none',
                        fontFamily: "'Poppins', sans-serif",
                        transition: 'all 0.2s ease',
                        boxSizing: 'border-box'
                      }}
                      className="admin-search-input"
                    />
                    <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', display: 'flex', alignItems: 'center' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                    </span>
                    {adminSearchQuery && (
                      <button
                        onClick={() => setAdminSearchQuery('')}
                        style={{
                          position: 'absolute',
                          right: '14px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          color: '#94a3b8',
                          fontSize: '18px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title="Clear search"
                      >
                        &times;
                      </button>
                    )}
                  </div>
                </div>

                {/* Products Table */}
                <div className="manager-table-wrapper">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                    <h4 className="table-heading" style={{ margin: 0 }}>Store Products ({filteredProducts.length})</h4>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <button 
                        type="button"
                        onClick={handleExportCSV} 
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 16px',
                          backgroundColor: '#f1f5f9',
                          color: '#334155',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7 10 12 15 17 10"></polyline>
                          <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        Export CSV
                      </button>
                      <label 
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 16px',
                          backgroundColor: '#be1e2d',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="17 8 12 3 7 8"></polyline>
                          <line x1="12" y1="3" x2="12" y2="15"></line>
                        </svg>
                        Import CSV
                        <input type="file" accept=".csv" onChange={handleImportCSV} style={{ display: 'none' }} />
                      </label>
                    </div>
                  </div>
                  {filteredProducts.length === 0 ? <p className="empty-catalog-text">No active products match search query.</p> : (
                    <table className="modern-saas-table">
                      <thead>
                        <tr>
                          <th>Product Preview</th>
                          <th>Name</th>
                          <th>Category</th>
                          <th>Retail Price (EUR)</th>
                          <th>B2B Price (EUR)</th>
                          <th>Stock</th>
                          <th>Rating</th>
                          <th>Options</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProducts.map((p) => (
                          <tr key={p._id || p.id}>
                            <td>
                              <div className="table-image-wrapper">
                                <img src={p.image} alt={p.name} className="table-cell-thumb" />
                              </div>
                            </td>
                            <td className="cell-emphasis">{p.name}</td>
                            <td><span className="table-cat-badge">{p.category}</span></td>
                            <td className="cell-emphasis">€{p.price}</td>
                            <td className="cell-emphasis">{p.b2bPrice ? `€${p.b2bPrice}` : '-'}</td>
                            <td>
                              <span className={`stock-badge ${p.stockStatus === 'Out of Stock' ? 'out-of-stock' : 'in-stock'}`}>
                                {p.stockStatus || 'In Stock'}
                              </span>
                            </td>
                            <td>⭐ {p.rating}</td>
                            <td>
                              <div className="table-actions-group">
                                <button onClick={() => handleStartEdit(p)} className="btn-table-edit">
                                  Edit
                                </button>
                                <button onClick={() => handleDeleteProduct(p._id || p.id)} className="btn-table-delete">
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Brand Products Table */}
                <div className="manager-table-wrapper">
                  <h4 className="table-heading">Brand Products ({filteredBrandProducts.length})</h4>
                  {filteredBrandProducts.length === 0 ? <p className="empty-catalog-text">No active brand products match search query.</p> : (
                    <table className="modern-saas-table">
                      <thead>
                        <tr>
                          <th>Product Preview</th>
                          <th>Name</th>
                          <th>Brand</th>
                          <th>Retail Price (EUR)</th>
                          <th>B2B Price (EUR)</th>
                          <th>Rating</th>
                          <th>Stock</th>
                          <th>Options</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredBrandProducts.map((p) => (
                          <tr key={p._id || p.id}>
                            <td>
                              <div className="table-image-wrapper">
                                <img src={p.image} alt={p.name} className="table-cell-thumb" />
                              </div>
                            </td>
                            <td className="cell-emphasis">{p.name}</td>
                            <td><span className="table-cat-badge">{p.brand}</span></td>
                            <td className="cell-emphasis">€{p.price}</td>
                            <td className="cell-emphasis">{p.b2bPrice ? `€${p.b2bPrice}` : '-'}</td>
                            <td>
                              <div className="table-cell-rating">
                                <span>⭐ {p.rating}</span>
                                <span className="table-cell-sub">({p.reviewsCount} reviews)</span>
                              </div>
                            </td>
                            <td>
                              <span className={`stock-badge ${p.stockStatus === 'Out of Stock' ? 'out-of-stock' : 'in-stock'}`}>
                                {p.stockStatus || 'In Stock'}
                              </span>
                            </td>
                            <td>
                              <div className="table-actions-group">
                                <button onClick={() => handleStartEditBrandProduct(p)} className="btn-table-edit">
                                  Edit
                                </button>
                                <button onClick={() => handleDeleteBrandProduct(p._id || p.id)} className="btn-table-delete">
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Services Table */}
                <div className="manager-table-wrapper">
                  <h4 className="table-heading">Store Services ({filteredServices.length})</h4>
                  {filteredServices.length === 0 ? <p className="empty-catalog-text">No active services match search query.</p> : (
                    <table className="modern-saas-table">
                      <thead>
                        <tr>
                          <th>Service Preview</th>
                          <th>Name</th>
                          <th>Brand Name</th>
                          <th>Model</th>
                          <th>Fault</th>
                          <th>Retail Price (EUR)</th>
                          <th>B2B Price (EUR)</th>
                          <th>Service Cost (EUR)</th>
                          <th>Shipping Charges (EUR)</th>
                          <th>Delivery Time</th>
                          <th>Stock</th>
                          <th>Options</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredServices.map((s) => (
                          <tr key={s._id || s.id}>
                            <td>
                              <div className="table-image-wrapper">
                                <img src={s.image} alt={s.name} className="table-cell-thumb" />
                              </div>
                            </td>
                            <td className="cell-emphasis">{s.name}</td>
                            <td><span className="table-cat-badge">{s.brandName}</span></td>
                            <td>{s.model}</td>
                            <td>{s.fault}</td>
                            <td className="cell-emphasis">€{s.price}</td>
                            <td className="cell-emphasis">{s.b2bPrice ? `€${s.b2bPrice}` : '-'}</td>
                            <td className="cell-emphasis">{s.serviceCost ? `€${s.serviceCost}` : '-'}</td>
                            <td className="cell-emphasis">€{s.shippingCharges !== undefined ? s.shippingCharges : 0}</td>
                            <td>{s.deliveryTime}</td>
                            <td>
                              <span className={`stock-badge ${s.stockStatus === 'Out of Stock' ? 'out-of-stock' : 'in-stock'}`}>
                                {s.stockStatus || 'In Stock'}
                              </span>
                            </td>
                            <td>
                              <div className="table-actions-group">
                                <button onClick={() => handleStartEdit(s)} className="btn-table-edit">
                                  Edit
                                </button>
                                <button onClick={() => handleDeleteService(s._id || s.id)} className="btn-table-delete">
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Related Products Catalog Table */}
                <div className="manager-table-wrapper">
                  <h4 className="table-heading">Related Products Catalog ({filteredDbRelatedProducts.length})</h4>
                  {filteredDbRelatedProducts.length === 0 ? <p className="empty-catalog-text">No active related products match search query.</p> : (
                    <table className="modern-saas-table">
                      <thead>
                        <tr>
                          <th>Product Preview</th>
                          <th>Name</th>
                          <th>Category</th>
                          <th>Retail Price (EUR)</th>
                          <th>B2B Price (EUR)</th>
                          <th>Stock</th>
                          <th>Rating</th>
                          <th>Options</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDbRelatedProducts.map((p) => (
                          <tr key={p._id || p.id}>
                            <td>
                              <div className="table-image-wrapper">
                                <img src={p.image} alt={p.name} className="table-cell-thumb" />
                              </div>
                            </td>
                            <td className="cell-emphasis">{p.name}</td>
                            <td><span className="table-cat-badge">{p.category}</span></td>
                            <td className="cell-emphasis">€{p.price}</td>
                            <td className="cell-emphasis">{p.b2bPrice ? `€${p.b2bPrice}` : '-'}</td>
                            <td>
                              <span className={`stock-badge ${p.stockStatus === 'Out of Stock' ? 'out-of-stock' : 'in-stock'}`}>
                                {p.stockStatus || 'In Stock'}
                              </span>
                            </td>
                            <td>⭐ {p.rating}</td>
                            <td>
                              <div className="table-actions-group">
                                <button onClick={() => handleDeleteRelatedProduct(p._id || p.id)} className="btn-table-delete">
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Categories Display */}
                <div className="manager-table-wrapper category-inventory">
                  <h4 className="table-heading">Active Categories ({filteredCategories.length})</h4>
                  {filteredCategories.length === 0 ? <p className="empty-catalog-text">No categories match search query.</p> : (
                    <div className="categories-grid-cards">
                      {filteredCategories.map((c) => (
                        <div key={c._id || c.name} className="cat-inventory-card">
                          <div className="cat-meta-left">
                            {c.brandLogo ? (
                              <img src={c.brandLogo} alt={c.name} className="cat-logo-sm" style={{ width: '28px', height: '28px', objectFit: 'contain', marginRight: '10px', borderRadius: '4px' }} />
                            ) : c.icon && typeof c.icon === 'string' && c.icon.trim().startsWith('<svg') ? (
                              <div className="cat-svg-sm" dangerouslySetInnerHTML={{ __html: c.icon }} />
                            ) : (
                              <div className="cat-svg-sm">{c.icon}</div>
                            )}
                            <span className="cat-name-span">{c.name}</span>
                          </div>
                          <button onClick={() => handleDeleteCategory(c._id || c.name)} className="btn-cat-remove" title="Remove Category">
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Brands Display */}
                <div className="manager-table-wrapper category-inventory">
                  <h4 className="table-heading">Active Brands ({filteredBrands.length})</h4>
                  {filteredBrands.length === 0 ? <p className="empty-catalog-text">No brands match search query.</p> : (
                    <div className="categories-grid-cards">
                      {filteredBrands.map((b) => (
                        <div key={b._id || b.name} className="cat-inventory-card">
                          <div className="cat-meta-left">
                            {b.image ? (
                              <img src={b.image} alt={b.name} className="cat-logo-sm" style={{ width: '28px', height: '28px', objectFit: 'contain', marginRight: '10px', borderRadius: '4px' }} />
                            ) : (
                              <div className="cat-svg-sm" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7fafc', width: '28px', height: '28px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold' }}>
                                {b.name.substring(0, 2).toUpperCase()}
                              </div>
                            )}
                            <span className="cat-name-span">{b.name}</span>
                          </div>
                          <button onClick={() => handleDeleteBrand(b._id || b.name)} className="btn-cat-remove" title="Remove Brand">
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* New Arrivals Display */}
                <div className="manager-table-wrapper">
                  <h4 className="table-heading">Homepage Arrivals ({filteredArrivals.length})</h4>
                  {filteredArrivals.length === 0 ? <p className="empty-catalog-text">No new arrivals banners match search query.</p> : (
                    <table className="modern-saas-table">
                      <thead>
                        <tr>
                          <th>Banner Preview</th>
                          <th>Title</th>
                          <th>Layout Position</th>
                          <th>Subtitle Description</th>
                          <th>Options</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredArrivals.map((a) => (
                          <tr key={a._id}>
                            <td>
                              <div className="table-image-wrapper wide">
                                <img src={a.image} alt={a.title} className="table-cell-thumb wide" />
                              </div>
                            </td>
                            <td className="cell-emphasis">{a.title}</td>
                            <td><span className="table-layout-style-badge">{a.layoutStyle}</span></td>
                            <td className="cell-desc-text">{a.description}</td>
                            <td>
                              <button onClick={() => handleDeleteArrival(a._id)} className="btn-table-delete">
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

              </div>
            );
          })()}

          {/* ADD SHIPPING PANEL */}
          {activeTab === 'shipping' && (
            <div className="database-manager-layout animate-in">
              <h3 className="section-panel-title">Shipping Management</h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', alignItems: 'start' }}>
                <form onSubmit={handleShippingSubmit} className="dashboard-grid-form" style={{ background: 'var(--nexus-surface)', padding: '24px', borderRadius: '8px', border: '1px solid var(--nexus-border)', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  <h4 className="table-heading" style={{ margin: 0 }}>Update Shipping Charges</h4>
                  
                  <div className="form-group-full">
                    <label className="field-label">Shipping Charge (EUR) <span className="req">*</span></label>
                    <input
                      type="number"
                      step="0.01;any"
                      min="0"
                      value={shippingInput}
                      onChange={(e) => setShippingInput(e.target.value)}
                      placeholder="e.g. 10.00"
                      required
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '6px', border: '1px solid var(--nexus-border-dark)', background: 'var(--nexus-surface)', color: 'var(--nexus-text-main)' }}
                    />
                  </div>
                  
                  <div className="form-submit-row" style={{ margin: 0 }}>
                    <button type="submit" className="action-submit-btn" disabled={loading}>
                      {loading ? 'Saving...' : 'Save Shipping Charges'}
                    </button>
                  </div>
                </form>

                <div style={{ background: 'var(--nexus-surface)', padding: '24px', borderRadius: '8px', border: '1px solid var(--nexus-border)', color: 'var(--nexus-text-main)', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  <h4 className="table-heading" style={{ margin: 0 }}>Current Settings</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '10px', borderBottom: '1px solid var(--nexus-border)' }}>
                      <span style={{ fontWeight: '500' }}>Active Shipping Charge:</span>
                      <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#be1e2d' }}>€{(Number(shippingCharge) || 0).toFixed(2)}</span>
                    </div>
                    <p style={{ fontSize: '0.85rem', color: '#a0aec0', lineHeight: '1.5', margin: 0 }}>
                      This shipping charge will automatically be fetched and applied at checkout/cart calculation on the frontend store.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* CUSTOMER ORDERS PANEL */}
          {activeTab === 'orders' && (() => {
            const filteredOrders = orders.filter(o => {
              const query = orderSearchQuery.toLowerCase();
              const orderIdShort = o._id.substring(o._id.length - 6).toLowerCase();
              const orderIdFull = o._id.toLowerCase();
              const name = (o.fullName || '').toLowerCase();
              const address = `${o.address1 || ''} ${o.city || ''} ${o.zipCode || ''}`.toLowerCase();
              const status = (o.status || 'Pending').toLowerCase();
              const email = (o.email || '').toLowerCase();
              const phone = (o.phone || '').toLowerCase();
              return (
                orderIdShort.includes(query) ||
                orderIdFull.includes(query) ||
                name.includes(query) ||
                address.includes(query) ||
                status.includes(query) ||
                email.includes(query) ||
                phone.includes(query)
              );
            });
            return (
              <div className="database-manager-layout animate-in">
                <h3 className="section-panel-title">Customer Orders Panel</h3>
                <div className="manager-table-wrapper">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
                    <h4 className="table-heading" style={{ margin: 0 }}>
                      All Orders ({filteredOrders.length})
                    </h4>
                    <div style={{ position: 'relative', width: '320px' }}>
                      <input
                        type="text"
                        placeholder="Search orders (ID, name, city, status...)"
                        value={orderSearchQuery}
                        onChange={(e) => setOrderSearchQuery(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px 16px 10px 40px',
                          borderRadius: '25px',
                          border: '1px solid var(--nexus-border-dark)',
                          backgroundColor: 'var(--nexus-surface)',
                          color: 'var(--nexus-text-main)',
                          fontSize: '0.9rem',
                          fontFamily: 'var(--nexus-font)',
                          outline: 'none',
                          transition: 'var(--nexus-transition)',
                          boxSizing: 'border-box'
                        }}
                        className="admin-search-input"
                      />
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                          position: 'absolute',
                          left: '15px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: 'var(--nexus-text-sub)'
                        }}
                      >
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                      {orderSearchQuery && (
                        <button
                          onClick={() => setOrderSearchQuery('')}
                          style={{
                            position: 'absolute',
                            right: '15px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            border: 'none',
                            background: 'none',
                            color: 'var(--nexus-text-sub)',
                            cursor: 'pointer',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  {orders.length === 0 ? (
                    <p className="empty-catalog-text">No orders placed yet.</p>
                  ) : filteredOrders.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--nexus-text-sub)', background: 'var(--nexus-surface)', border: '1px solid var(--nexus-border-dark)', borderRadius: '12px' }}>
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '12px', opacity: 0.7 }}>
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                      <p style={{ margin: 0, fontSize: '1rem', fontWeight: '500' }}>No matching orders found</p>
                      <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem' }}>Try adjusting your search query or keywords.</p>
                    </div>
                  ) : (
                    <table className="modern-saas-table orders-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Address</th>
                          <th>Date</th>
                          <th>Price</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOrders.map((o) => (
                          <tr key={o._id}>
                            <td className="cell-id">#{o._id.substring(o._id.length - 6).toUpperCase()}</td>
                            <td className="cell-emphasis">{o.fullName}</td>
                            <td className="cell-address-text">{o.address1}, {o.city}, {o.zipCode}</td>
                            <td>{new Date(o.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                            <td className="cell-emphasis">€{o.total.toFixed(2)}</td>
                            <td>
                              <select
                                value={o.status || 'Pending'}
                                onChange={(e) => handleUpdateOrderStatus(o._id, e.target.value)}
                                className={`status-select-badge ${o.status?.toLowerCase() || 'pending'}`}
                              >
                                <option value="Pending">Pending</option>
                                <option value="Dispatch">Dispatch</option>
                                <option value="Completed">Completed</option>
                              </select>
                            </td>
                            <td>
                              <div className="table-actions-group">
                                <button onClick={() => setSelectedOrder(o)} className="btn-table-view" title="See Details" type="button">
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                    <circle cx="12" cy="12" r="3"></circle>
                                  </svg>
                                </button>
                                <button onClick={() => handleDeleteOrder(o._id)} className="btn-table-delete" title="Delete Order" type="button">
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6"></polyline>
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                    <line x1="10" y1="11" x2="10" y2="17"></line>
                                    <line x1="14" y1="11" x2="14" y2="17"></line>
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            );
          })()}

        </div>
      </main>

      {/* ORDER DETAILS OVERLAY MODAL */}
      {selectedOrder && (
        <div className="admin-modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="admin-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Order Detail - #{selectedOrder._id.substring(selectedOrder._id.length - 6).toUpperCase()}</h3>
              <button className="modal-close-btn" onClick={() => setSelectedOrder(null)}>&times;</button>
            </div>

            <div className="modal-body">
              <div className="modal-info-grid">
                <div>
                  <h4 className="modal-section-title">Customer Information</h4>
                  <p><strong>Name:</strong> {selectedOrder.fullName}</p>
                  <p><strong>Email:</strong> {selectedOrder.email}</p>
                  <p><strong>Shipping Method:</strong> {selectedOrder.shippingMethod || 'Shipping on delivery'}</p>
                  <p><strong>Date:</strong> {new Date(selectedOrder.createdAt).toLocaleString()}</p>
                  <p><strong>Payment Method:</strong> {selectedOrder.paymentMethod || 'Stripe'}</p>
                  <p><strong>Payment Status:</strong> {selectedOrder.paymentStatus || 'Pending'}</p>
                  <p>
                    <strong>Invoice:</strong>{' '}
                    {selectedOrder.moneybirdInvoiceNumber
                      ? `#${selectedOrder.moneybirdInvoiceNumber}`
                      : '—'}
                    {' '}
                    <span style={{ textTransform: 'capitalize' }}>
                      ({selectedOrder.moneybirdStatus && selectedOrder.moneybirdStatus !== 'none'
                        ? selectedOrder.moneybirdStatus
                        : 'not synced'})
                    </span>
                  </p>
                  {selectedOrder.moneybirdStatus === 'failed' && selectedOrder.moneybirdError && (
                    <p style={{ color: '#be1e2d', fontSize: '0.85rem' }}>{selectedOrder.moneybirdError}</p>
                  )}
                  {selectedOrder.paymentMethod === 'Bank Transfer' && selectedOrder.paymentStatus !== 'Paid' && (
                    <p>
                      <button
                        type="button"
                        className="btn-table-view"
                        disabled={moneybirdBusy}
                        onClick={() => handleMarkOrderPaid(selectedOrder._id)}
                      >
                        {moneybirdBusy ? 'Updating…' : 'Mark as Paid'}
                      </button>
                    </p>
                  )}
                  {(!selectedOrder.moneybirdInvoiceId
                    || selectedOrder.moneybirdStatus === 'failed'
                    || (selectedOrder.paymentStatus === 'Paid' && selectedOrder.moneybirdStatus !== 'paid')) && (
                    <p>
                      <button
                        type="button"
                        className="btn-table-view"
                        disabled={moneybirdBusy}
                        onClick={() => handleRetryMoneybird(selectedOrder._id)}
                      >
                        {moneybirdBusy ? 'Retrying…' : 'Retry invoice'}
                      </button>
                    </p>
                  )}
                  <p>
                    <strong>Status:</strong>{' '}
                    <select
                      value={selectedOrder.status || 'Pending'}
                      onChange={(e) => handleUpdateOrderStatus(selectedOrder._id, e.target.value)}
                      className={`status-select-badge inline ${selectedOrder.status?.toLowerCase() || 'pending'}`}
                    >
                      <option value="Pending">Pending</option>
                      <option value="Dispatch">Dispatch</option>
                      <option value="Completed">Completed</option>
                    </select>
                  </p>
                </div>
                <div>
                  <h4 className="modal-section-title">Shipping Address</h4>
                  <p><strong>Street:</strong> {selectedOrder.address1}</p>
                  {selectedOrder.address2 && <p><strong>Address Line 2:</strong> {selectedOrder.address2}</p>}
                  <p><strong>City:</strong> {selectedOrder.city}</p>
                  <p><strong>State / Region:</strong> {selectedOrder.stateProv || '-'}</p>
                  <p><strong>Zip Code:</strong> {selectedOrder.zipCode}</p>
                  <p><strong>Country:</strong> {selectedOrder.country}</p>
                </div>
              </div>

              <hr className="modal-divider" />

              <h4 className="modal-section-title">Purchased Products</h4>
              <div className="modal-products-list">
                <table className="modal-products-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Qty</th>
                      <th>Unit Price</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOrder.items.map((item, idx) => (
                      <tr key={idx}>
                        <td>
                          <div className="modal-product-cell">
                            <span>{item.name}</span>
                            {item.deliveryTime && (
                              <span className="modal-product-delivery" style={{ display: 'block', fontSize: '0.75rem', color: '#a0aec0', marginTop: '2px' }}>
                                Delivery: {item.deliveryTime}
                              </span>
                            )}
                            <span className="modal-product-cat">{item.category}</span>
                          </div>
                        </td>
                        <td>{item.quantity}</td>
                        <td>€{item.price.toFixed(2)}</td>
                        <td>€{(item.price * item.quantity).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="modal-totals-summary">
                <div className="totals-row">
                  <span>Subtotal:</span>
                  <span>€{selectedOrder.subtotal.toFixed(2)}</span>
                </div>
                <div className="totals-row">
                  <span>Shipping:</span>
                  <span>{selectedOrder.shipping === 0 ? 'FREE' : `€${selectedOrder.shipping.toFixed(2)}`}</span>
                </div>
                <div className="totals-row total-highlight">
                  <span>Grand Total:</span>
                  <span>€{selectedOrder.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="modal-close-action-btn" onClick={() => setSelectedOrder(null)} type="button">Close</button>
            </div>
          </div>
        </div>
      )}
      {/* CSV Import Verification Preview Modal */}
      {showImportPreviewModal && importVerificationData && (
        <div className="admin-modal-overlay" onClick={() => setShowImportPreviewModal(false)}>
          <div 
            className="admin-modal-content csv-preview-modal animate-in" 
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '1000px', width: '95%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', backgroundColor: '#ffffff', borderRadius: '12px', overflow: 'hidden' }}
          >
            {/* Modal Header */}
            <div className="admin-modal-header" style={{ padding: '24px 32px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>
                  CSV Import Verification & Preview
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                  Review the validated product details below before confirming the import.
                </p>
              </div>
              <button 
                onClick={() => setShowImportPreviewModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '24px', color: '#64748b', cursor: 'pointer' }}
              >
                &times;
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
              {/* Stat Cards Summary */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                <div style={{ padding: '12px 16px', borderRadius: '10px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                  <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Total Rows</span>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a', marginTop: '2px' }}>{importVerificationData.totalRows}</div>
                </div>
                <div style={{ padding: '12px 16px', borderRadius: '10px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', textAlign: 'center' }}>
                  <span style={{ fontSize: '11px', color: '#15803d', fontWeight: '600', textTransform: 'uppercase' }}>New To Add</span>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: '#166534', marginTop: '2px' }}>{importVerificationData.addedCount}</div>
                </div>
                <div style={{ padding: '12px 16px', borderRadius: '10px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', textAlign: 'center' }}>
                  <span style={{ fontSize: '11px', color: '#1d4ed8', fontWeight: '600', textTransform: 'uppercase' }}>To Update</span>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e40af', marginTop: '2px' }}>{importVerificationData.updatedCount}</div>
                </div>
                <div style={{ padding: '12px 16px', borderRadius: '10px', backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1', textAlign: 'center' }}>
                  <span style={{ fontSize: '11px', color: '#475569', fontWeight: '600', textTransform: 'uppercase' }}>Skipped</span>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: '#334155', marginTop: '2px' }}>{importVerificationData.skippedCount}</div>
                </div>
                {importVerificationData.errorCount > 0 && (
                  <div style={{ padding: '12px 16px', borderRadius: '10px', backgroundColor: '#fff1f2', border: '1px solid #fecdd3', textAlign: 'center' }}>
                    <span style={{ fontSize: '11px', color: '#b91c1c', fontWeight: '600', textTransform: 'uppercase' }}>Errors</span>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: '#991b1b', marginTop: '2px' }}>{importVerificationData.errorCount}</div>
                  </div>
                )}
              </div>

              {/* Filter Tabs */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>
                {[
                  { key: 'ALL', label: `All Items (${importVerificationData.previewItems.length})` },
                  { key: 'NEW', label: `New (${importVerificationData.addedCount})` },
                  { key: 'UPDATE', label: `Updates (${importVerificationData.updatedCount})` },
                  { key: 'SKIP', label: `Skipped (${importVerificationData.skippedCount})` },
                  ...(importVerificationData.errorCount > 0 ? [{ key: 'ERROR', label: `Errors (${importVerificationData.errorCount})` }] : [])
                ].map(tab => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setPreviewFilterTab(tab.key)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '600',
                      border: 'none',
                      cursor: 'pointer',
                      backgroundColor: previewFilterTab === tab.key ? '#be1e2d' : '#f1f5f9',
                      color: previewFilterTab === tab.key ? '#ffffff' : '#475569',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Preview Table */}
              <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#475569' }}>
                      <th style={{ padding: '10px 12px' }}>Row</th>
                      <th style={{ padding: '10px 12px' }}>Product Name</th>
                      <th style={{ padding: '10px 12px' }}>Category</th>
                      <th style={{ padding: '10px 12px' }}>Price</th>
                      <th style={{ padding: '10px 12px' }}>Action Status</th>
                      <th style={{ padding: '10px 12px' }}>Details / Changes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importVerificationData.previewItems
                      .filter(item => previewFilterTab === 'ALL' || item.status === previewFilterTab)
                      .map((item, idx) => {
                        let badgeStyle = { backgroundColor: '#f1f5f9', color: '#475569' };
                        if (item.status === 'NEW') badgeStyle = { backgroundColor: '#dcfce7', color: '#15803d' };
                        if (item.status === 'UPDATE') badgeStyle = { backgroundColor: '#dbeafe', color: '#1d4ed8' };
                        if (item.status === 'ERROR') badgeStyle = { backgroundColor: '#ffe4e6', color: '#be123c' };

                        return (
                          <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: '#ffffff', transition: 'background-color 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}>
                            <td style={{ padding: '12px', color: '#64748b', fontWeight: '500' }}>#{item.rowNumber}</td>
                            <td style={{ padding: '12px', fontWeight: '600', color: '#0f172a' }}>{item.name}</td>
                            <td style={{ padding: '12px', color: '#475569' }}>{item.category || '-'}</td>
                            <td style={{ padding: '12px', fontWeight: '600', color: '#0f172a' }}>
                              {item.price ? `€${item.price}` : '-'}
                            </td>
                            <td style={{ padding: '12px' }}>
                              <span style={{
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: '700',
                                textTransform: 'uppercase',
                                letterSpacing: '0.03em',
                                ...badgeStyle
                              }}>
                                {item.status}
                              </span>
                            </td>
                            <td style={{ padding: '12px', fontSize: '12px', color: '#64748b' }}>
                              {item.reason}
                              {item.changes && item.changes.length > 0 && (
                                <div style={{ marginTop: '4px', color: '#1d4ed8', fontWeight: '500' }}>
                                  {item.changes.join(' | ')}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc', borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
              <button 
                type="button"
                onClick={() => setShowImportPreviewModal(false)}
                style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: '#ffffff', color: '#475569', fontWeight: '600', cursor: 'pointer' }}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleConfirmImportExecution}
                disabled={importConfirming || (importVerificationData.addedCount === 0 && importVerificationData.updatedCount === 0)}
                style={{
                  padding: '10px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: (importVerificationData.addedCount === 0 && importVerificationData.updatedCount === 0) ? '#94a3b8' : '#be1e2d',
                  color: '#ffffff',
                  fontWeight: '700',
                  fontSize: '14px',
                  cursor: (importVerificationData.addedCount === 0 && importVerificationData.updatedCount === 0) ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                {importConfirming ? (
                  <>Processing Import...</>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    Confirm & Import ({importVerificationData.addedCount + importVerificationData.updatedCount} Products)
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
